import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createEdge, createEmptyDocument, createNode, createWelcomeDocument } from './core/defaults';
import { SHAPE_BY_TYPE } from './core/shapeRegistry';
import { DIAGRAM_TEMPLATES } from './core/templates';
import { duplicateDocument, getActivePage, updateActivePage } from './core/document';
import { anchorPoint, chooseAnchor, nodeBounds } from './core/geometry';
import { analyzePageDeletion, removePageWithLinkCleanup } from './core/pageImpact';
import { normalizeBpmnMembership } from './core/bpmn';
import { commit, createHistory, redo, undo, type HistoryState } from './core/history';
import type { AnchorSide, DiagramDocument, DiagramEdge, DiagramNode, DiagramPage, NodeStyle, NodeType, SelectionState, ViewportState } from './core/types';
import { autoLayout, type LayoutMode } from './services/autoLayout';
import { detectDeviceCapabilities } from './services/deviceCapabilities';
import { adoptRemoteDocument, countPendingSync, deleteLocalDocument, listLocalDocuments, listSyncOutbox, loadLatestLocal, loadLocalDocument, loadRecoverySession, markDocumentSynced, queueDocumentForSync, saveEmergencySnapshot, saveLocal, saveRecoverySession, setPersistenceScope } from './services/persistence/localRepository';
import { loadEditorPreferences, saveEditorPreferences } from './services/persistence/preferences';
import { deleteCustomSymbol, exportCustomSymbolsJson, importCustomSymbolsJson, loadCustomSymbols, saveCustomSymbol, type CustomSymbolPreset } from './services/persistence/customLibrary';
import { deleteCustomTemplate, exportCustomTemplatesJson, importCustomTemplatesJson, instantiateCustomTemplate, loadCustomTemplates, saveCustomTemplate, type CustomTemplatePreset } from './services/persistence/customTemplates';
import { exportWorkspaceBackup, importWorkspaceBackup } from './services/persistence/workspaceBackup';
import { downloadBlob } from './services/export/download';
import { DiagramCanvas } from './components/DiagramCanvas';
import { PageTabs } from './components/PageTabs';
import { PropertiesPanel } from './components/PropertiesPanel';
import { ShapePalette, type LibraryTab } from './components/ShapePalette';
import { StatusBar } from './components/StatusBar';
import { TopBar } from './components/TopBar';
import type { CommandAction } from './components/CommandPalette';
import type { ProjectSearchResult } from './components/ProjectSearch';
import { CanvasContextMenu, type CanvasContextRequest } from './components/CanvasContextMenu';
import { AnnouncementCenter } from './components/AnnouncementCenter';
import { LoginScreen } from './components/LoginScreen';
import { PublicShareViewer } from './components/PublicShareViewer';
import { validateDocumentSemantics, type DiagramIssue } from './core/semanticValidation';
import { importOrgCsv } from './services/import/orgCsv';
import { importSqlSchema } from './services/import/sqlSchema';
import { importNetworkCsv } from './services/import/networkCsv';
import { bindDataCsv } from './services/import/dataCsv';
import { importMermaid } from './services/interchange/mermaid';
import { importPlantUml } from './services/interchange/plantuml';
import { importDot } from './services/interchange/dot';
import { importBpmnXml } from './services/interchange/bpmnXml';
import { importCloudCsv } from './services/import/cloudCsv';
import { ServerApi, type AccountInfo, type Announcement, type OrganizationTemplate, type PublicSharePayload, type RemoteProjectSummary, type ServerOrganization, type ServerRevisionSummary, type ServerSession, type UserNotification } from './services/sync/ServerApi';
import { SyncGateway, SyncGatewayError } from './services/sync/SyncGateway';
import { flushSyncOutbox, pushSyncRecordWithRecovery, type SyncFlushResult } from './services/sync/SyncManager';
import { RealtimeProjectClient, type RealtimePresenceUser } from './services/sync/RealtimeProjectClient';
import { applyRemoteLiveOperations, deriveLiveOperations, isLiveOperationArray } from './services/sync/liveOperations';

const ProjectManager = lazy(() => import('./components/ProjectManager').then((module) => ({ default: module.ProjectManager })));
const ServerPanel = lazy(() => import('./components/ServerPanel').then((module) => ({ default: module.ServerPanel })));
const CommentsPanel = lazy(() => import('./components/CommentsPanel').then((module) => ({ default: module.CommentsPanel })));
const RevisionComparePanel = lazy(() => import('./components/RevisionComparePanel').then((module) => ({ default: module.RevisionComparePanel })));
const MasterPanel = lazy(() => import('./components/MasterPanel').then((module) => ({ default: module.MasterPanel })));
const SharePanel = lazy(() => import('./components/SharePanel').then((module) => ({ default: module.SharePanel })));
const HomeDashboard = lazy(() => import('./components/HomeDashboard').then((module) => ({ default: module.HomeDashboard })));
const AccountPanel = lazy(() => import('./components/AccountPanel').then((module) => ({ default: module.AccountPanel })));
const NotificationCenter = lazy(() => import('./components/NotificationCenter').then((module) => ({ default: module.NotificationCenter })));
const CommandPalette = lazy(() => import('./components/CommandPalette').then((module) => ({ default: module.CommandPalette })));
const LayersManager = lazy(() => import('./components/LayersManager').then((module) => ({ default: module.LayersManager })));
const ValidationPanel = lazy(() => import('./components/ValidationPanel').then((module) => ({ default: module.ValidationPanel })));
const ProjectSearch = lazy(() => import('./components/ProjectSearch').then((module) => ({ default: module.ProjectSearch })));
const HistoryPanel = lazy(() => import('./components/HistoryPanel').then((module) => ({ default: module.HistoryPanel })));
const HelpPanel = lazy(() => import('./components/HelpPanel').then((module) => ({ default: module.HelpPanel })));
const CollaboratorsPanel = lazy(() => import('./components/CollaboratorsPanel').then((module) => ({ default: module.CollaboratorsPanel })));

const INITIAL_VIEWPORT: ViewportState = { zoom: 1, panX: 70, panY: 70 };
const EMPTY_SELECTION: SelectionState = { nodeIds: [], edgeIds: [] };

function defaultServerBaseUrl(): string {
  try {
    const stored = localStorage.getItem('owdex-draw:server-url');
    if (stored) return stored;
  } catch { /* armazenamento pode estar indisponível */ }
  if (typeof window !== 'undefined') {
    const local = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (!local && window.location.protocol === 'https:') return window.location.origin;
  }
  return 'http://127.0.0.1:8787';
}

const SESSION_STORAGE_KEY = 'owdex-draw:server-session';
function productionLoginRequired(): boolean {
  if (typeof window === 'undefined') return false;
  return !['localhost', '127.0.0.1'].includes(window.location.hostname);
}
function currentPublicShareToken(): string | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('share')?.trim() ?? '';
  return /^[A-Za-z0-9_-]{20,200}$/.test(value) ? value : null;
}

type SaveStatus = 'saved' | 'saving' | 'error';

interface ClipboardPayload {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
}

function patchDocumentNode(document: DiagramDocument, id: string, patch: Partial<DiagramNode>): DiagramDocument {
  return updateActivePage(document, (page) => ({ ...page, nodes: page.nodes.map((node) => node.id === id ? { ...node, ...patch } : node) }));
}

function patchDocumentEdge(document: DiagramDocument, id: string, patch: Partial<DiagramEdge>): DiagramDocument {
  return updateActivePage(document, (page) => ({ ...page, edges: page.edges.map((edge) => edge.id === id ? { ...edge, ...patch } : edge) }));
}

function documentObjectCount(document: DiagramDocument): number {
  return document.pages.reduce((total, page) => total + page.nodes.length + page.edges.length, 0);
}

function comparableDocument(document: DiagramDocument): string {
  return JSON.stringify({ ...document, updatedAt: '' });
}

function documentsHaveSameContent(left: DiagramDocument, right: DiagramDocument): boolean {
  return comparableDocument(left) === comparableDocument(right);
}

function selectedNodes(page: DiagramPage, selection: SelectionState): DiagramNode[] {
  const ids = new Set(selection.nodeIds);
  return page.nodes.filter((node) => ids.has(node.id));
}

type BundleMode = { kind: 'source' | 'target'; nodeId: string; edges: DiagramEdge[] } | null;

function bundleModeForEdges(edges: DiagramEdge[]): BundleMode {
  if (edges.length < 2) return null;
  const sourceId = edges[0].sourceId;
  if (edges.every((edge) => edge.sourceId === sourceId)) return { kind: 'source', nodeId: sourceId, edges };
  const targetId = edges[0].targetId;
  if (edges.every((edge) => edge.targetId === targetId)) return { kind: 'target', nodeId: targetId, edges };
  return null;
}

function isVerticalBundleSide(side: AnchorSide): boolean {
  return String(side).startsWith('top') || String(side).startsWith('bottom');
}

function bundleSide(mode: Exclude<BundleMode, null>): AnchorSide {
  const first = mode.edges[0];
  return (mode.kind === 'source' ? first.sourceAnchor : first.targetAnchor) ?? 'auto';
}

function inferBundleSide(mode: Exclude<BundleMode, null>, nodes: DiagramNode[]): Exclude<AnchorSide, 'auto'> {
  const common = nodes.find((node) => node.id === mode.nodeId);
  if (!common) return 'right';
  const explicit = bundleSide(mode);
  if (explicit !== 'auto') return explicit;
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const others = mode.edges.map((edge) => nodeById.get(mode.kind === 'source' ? edge.targetId : edge.sourceId)).filter((node): node is DiagramNode => Boolean(node));
  if (!others.length) return 'right';
  const commonCenter = { x: common.x + common.width / 2, y: common.y + common.height / 2 };
  const average = others.reduce((acc, node) => ({ x: acc.x + node.x + node.width / 2, y: acc.y + node.y + node.height / 2 }), { x: 0, y: 0 });
  average.x /= others.length; average.y /= others.length;
  const dx = average.x - commonCenter.x, dy = average.y - commonCenter.y;
  return Math.abs(dx) >= Math.abs(dy) ? (dx >= 0 ? 'right' : 'left') : (dy >= 0 ? 'bottom' : 'top');
}

function repairBundledEdgesAfterGeometry(before: DiagramPage, nextNodes: DiagramNode[], edges: DiagramEdge[]): DiagramEdge[] {
  const beforeNodes = new Map(before.nodes.map((node) => [node.id, node]));
  const nextNodeById = new Map(nextNodes.map((node) => [node.id, node]));
  const groups = new Map<string, DiagramEdge[]>();
  for (const edge of edges) if (edge.bundleId) groups.set(edge.bundleId, [...(groups.get(edge.bundleId) ?? []), edge]);
  if (!groups.size) return edges;
  const replacements = new Map<string, DiagramEdge>();
  for (const group of groups.values()) {
    const mode = bundleModeForEdges(group);
    if (!mode) continue;
    const beforeCommon = beforeNodes.get(mode.nodeId);
    const nextCommon = nextNodeById.get(mode.nodeId);
    if (!beforeCommon || !nextCommon) continue;
    const side = inferBundleSide(mode, nextNodes);
    const oldCommonAnchor = anchorPoint(beforeCommon, side);
    const nextCommonAnchor = anchorPoint(nextCommon, side);
    const firstWaypoints = mode.edges[0].waypoints ?? [];
    const oldJunction = mode.kind === 'source' ? firstWaypoints[0] : firstWaypoints[firstWaypoints.length - 1];
    if (!oldJunction) continue;
    const vertical = isVerticalBundleSide(side);
    const junction = {
      x: vertical ? nextCommonAnchor.x : oldJunction.x + (nextCommonAnchor.x - oldCommonAnchor.x),
      y: vertical ? oldJunction.y + (nextCommonAnchor.y - oldCommonAnchor.y) : nextCommonAnchor.y,
    };
    for (const edge of mode.edges) {
      const other = nextNodeById.get(mode.kind === 'source' ? edge.targetId : edge.sourceId);
      if (!other) continue;
      const otherSide = (mode.kind === 'source' ? edge.targetAnchor : edge.sourceAnchor) ?? 'auto';
      const resolvedOtherSide = otherSide === 'auto' ? chooseAnchor(other, nextCommon) : otherSide;
      const otherAnchor = anchorPoint(other, resolvedOtherSide);
      const branch = vertical ? { x: otherAnchor.x, y: junction.y } : { x: junction.x, y: otherAnchor.y };
      replacements.set(edge.id, { ...edge, routingMode: 'manual', waypoints: mode.kind === 'source' ? [junction, branch] : [branch, junction] });
    }
  }
  return edges.map((edge) => replacements.get(edge.id) ?? edge);
}

export default function App() {
  const [history, setHistory] = useState<HistoryState<DiagramDocument>>(() => createHistory(createWelcomeDocument()));
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [viewport, setViewport] = useState<ViewportState>(INITIAL_VIEWPORT);
  const [connectionMode, setConnectionMode] = useState(false);
  const [connectionSourceId, setConnectionSourceId] = useState<string | null>(null);
  const [connectionSourceAnchor, setConnectionSourceAnchor] = useState<AnchorSide>('auto');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [loaded, setLoaded] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [commandsOpen, setCommandsOpen] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [contextMenu, setContextMenu] = useState<CanvasContextRequest | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [serverPanelOpen, setServerPanelOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [serverBaseUrl, setServerBaseUrl] = useState(defaultServerBaseUrl);
  const [serverSession, setServerSession] = useState<ServerSession | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementBusy, setAnnouncementBusy] = useState(false);
  const [serverOrganizations, setServerOrganizations] = useState<ServerOrganization[]>([]);
  const [syncOrganizationId, setSyncOrganizationId] = useState<string>('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncLastResult, setSyncLastResult] = useState<SyncFlushResult | null>(null);
  const [remoteProjects, setRemoteProjects] = useState<RemoteProjectSummary[]>([]);
  const [localProjects, setLocalProjects] = useState<DiagramDocument[]>([]);
  const [syncConflicts, setSyncConflicts] = useState<string[]>([]);
  const [realtimeUsers, setRealtimeUsers] = useState<RealtimePresenceUser[]>([]);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [remoteUpdateAvailable, setRemoteUpdateAvailable] = useState(false);
  const [commentEventVersion, setCommentEventVersion] = useState(0);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const [revisionCompare, setRevisionCompare] = useState<{ local: DiagramDocument; remote: DiagramDocument; revision: string } | null>(null);
  const [masterOpen, setMasterOpen] = useState(false);
  const [collaboratorsOpen, setCollaboratorsOpen] = useState(false);
  const [preferences, setPreferences] = useState(() => loadEditorPreferences());
  const [customSymbols, setCustomSymbols] = useState<CustomSymbolPreset[]>(() => loadCustomSymbols());
  const [customTemplates, setCustomTemplates] = useState<CustomTemplatePreset[]>(() => loadCustomTemplates());
  const [organizationTemplates, setOrganizationTemplates] = useState<OrganizationTemplate[]>([]);
  const [mobileLibraryOpen, setMobileLibraryOpen] = useState(false);
  const [libraryTab, setLibraryTab] = useState<LibraryTab>('shapes');
  const [mobilePropertiesOpen, setMobilePropertiesOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editorShareProject, setEditorShareProject] = useState<RemoteProjectSummary | null>(null);
  const [dashboardOpen, setDashboardOpen] = useState(() => productionLoginRequired());
  const [accountOpen, setAccountOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [dashboardShareProject, setDashboardShareProject] = useState<RemoteProjectSummary | null>(null);

  type OverlayPanel = 'projects' | 'layers' | 'commands' | 'validation' | 'search' | 'history' | 'help' | 'server' | 'comments' | 'master' | 'collaborators' | 'share' | 'account' | 'notifications';
  const closeEditorOverlays = useCallback((except?: OverlayPanel) => {
    if (except !== 'projects') setProjectsOpen(false);
    if (except !== 'layers') setLayersOpen(false);
    if (except !== 'commands') setCommandsOpen(false);
    if (except !== 'validation') setValidationOpen(false);
    if (except !== 'search') setSearchOpen(false);
    if (except !== 'history') setHistoryOpen(false);
    if (except !== 'help') setHelpOpen(false);
    if (except !== 'server') setServerPanelOpen(false);
    if (except !== 'comments') setCommentsOpen(false);
    if (except !== 'master') setMasterOpen(false);
    if (except !== 'collaborators') setCollaboratorsOpen(false);
    if (except !== 'share') { setShareOpen(false); setEditorShareProject(null); }
    if (except !== 'account') setAccountOpen(false);
    if (except !== 'notifications') setNotificationOpen(false);
    setRevisionCompare(null);
  }, []);

  const openEditorOverlay = useCallback((panel: OverlayPanel) => {
    closeEditorOverlays(panel);
    setContextMenu(null);
    if (panel === 'projects') setProjectsOpen(true);
    else if (panel === 'layers') setLayersOpen(true);
    else if (panel === 'commands') setCommandsOpen(true);
    else if (panel === 'validation') setValidationOpen(true);
    else if (panel === 'search') setSearchOpen(true);
    else if (panel === 'history') setHistoryOpen(true);
    else if (panel === 'help') setHelpOpen(true);
    else if (panel === 'server') setServerPanelOpen(true);
    else if (panel === 'comments') setCommentsOpen(true);
    else if (panel === 'master') setMasterOpen(true);
    else if (panel === 'collaborators') setCollaboratorsOpen(true);
    else if (panel === 'share') setShareOpen(true);
    else if (panel === 'account') setAccountOpen(true);
    else if (panel === 'notifications') setNotificationOpen(true);
  }, [closeEditorOverlays]);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [presentationMode, setPresentationMode] = useState(false);
  const [authChecking, setAuthChecking] = useState(() => productionLoginRequired());
  const [publicShare, setPublicShare] = useState<PublicSharePayload | null>(null);
  const [publicShareLoading, setPublicShareLoading] = useState(() => Boolean(currentPublicShareToken()));
  const [publicShareError, setPublicShareError] = useState<string | null>(null);
  const clipboard = useRef<ClipboardPayload>({ nodes: [], edges: [] });
  const styleClipboard = useRef<NodeStyle | null>(null);
  const [styleClipboardReady, setStyleClipboardReady] = useState(false);
  const interactionBase = useRef<DiagramDocument | null>(null);
  const realtimeClient = useRef<RealtimeProjectClient | null>(null);
  const liveSyncChain = useRef<Promise<void>>(Promise.resolve());
  const remoteAppliedSync = useRef<{ id: string; updatedAt: string; revision: string } | null>(null);
  useEffect(() => {
    const shareToken = currentPublicShareToken();
    if (!shareToken) { setPublicShareLoading(false); return; }
    let cancelled = false;
    setPublicShareLoading(true); setPublicShareError(null);
    new ServerApi(serverBaseUrl).getPublicShare(shareToken)
      .then((payload) => { if (!cancelled) setPublicShare(payload); })
      .catch((error) => { if (!cancelled) setPublicShareError(error instanceof Error ? error.message : 'Não foi possível abrir o compartilhamento.'); })
      .finally(() => { if (!cancelled) setPublicShareLoading(false); });
    return () => { cancelled = true; };
  }, [serverBaseUrl]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const positionKey = 'owdex.draw.supportWidgetPosition';

    const important = (element: HTMLElement, property: string, value: string) => element.style.setProperty(property, value, 'important');

    const makeDraggable = (shell: HTMLElement, launcher: HTMLElement) => {
      if (shell.dataset.owdexDraggable === 'true') return;
      shell.dataset.owdexDraggable = 'true';
      try {
        const saved = JSON.parse(localStorage.getItem(positionKey) || 'null') as { left?: number; top?: number } | null;
        if (saved && Number.isFinite(saved.left) && Number.isFinite(saved.top)) {
          important(shell, 'left', `${Math.max(8, Math.min(window.innerWidth - 64, Number(saved.left)))}px`);
          important(shell, 'top', `${Math.max(8, Math.min(window.innerHeight - 64, Number(saved.top)))}px`);
          important(shell, 'right', 'auto'); important(shell, 'bottom', 'auto'); important(shell, 'transform', 'none');
        } else {
          important(shell, 'left', 'auto'); important(shell, 'top', 'auto'); important(shell, 'right', '18px'); important(shell, 'bottom', '76px'); important(shell, 'transform', 'none');
        }
      } catch { /* posição opcional */ }

      let active = false; let moved = false; let startX = 0; let startY = 0; let startLeft = 0; let startTop = 0;
      const onMove = (event: PointerEvent) => {
        if (!active) return;
        const dx = event.clientX - startX, dy = event.clientY - startY;
        if (!moved && Math.hypot(dx, dy) < 6) return;
        moved = true;
        event.preventDefault();
        const left = Math.max(8, Math.min(window.innerWidth - 62, startLeft + dx));
        const top = Math.max(8, Math.min(window.innerHeight - 62, startTop + dy));
        important(shell, 'left', `${left}px`); important(shell, 'top', `${top}px`); important(shell, 'right', 'auto'); important(shell, 'bottom', 'auto'); important(shell, 'transform', 'none');
      };
      const onUp = () => {
        if (!active) return;
        active = false;
        if (moved) {
          shell.dataset.owdexDragged = 'true';
          try { localStorage.setItem(positionKey, JSON.stringify({ left: shell.getBoundingClientRect().left, top: shell.getBoundingClientRect().top })); } catch { /* opcional */ }
        }
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      launcher.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        const rect = shell.getBoundingClientRect();
        active = true; moved = false; startX = event.clientX; startY = event.clientY; startLeft = rect.left; startTop = rect.top;
        try { launcher.setPointerCapture(event.pointerId); } catch { /* captura não é obrigatória */ }
        window.addEventListener('pointermove', onMove, { passive: false });
        window.addEventListener('pointerup', onUp, { once: true });
      });
      shell.addEventListener('click', (event) => {
        if (shell.dataset.owdexDragged === 'true') {
          shell.dataset.owdexDragged = 'false';
          event.preventDefault(); event.stopPropagation();
        }
      }, true);
    };

    function rootsFrom(root: Document | ShadowRoot): Array<Document | ShadowRoot> {
      const roots: Array<Document | ShadowRoot> = [root];
      for (const element of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
        if (element.shadowRoot) roots.push(...rootsFrom(element.shadowRoot));
      }
      return roots;
    }

    function ensureDragGrip(shell: HTMLElement) {
      if (shell.querySelector(':scope > .owdex-chat-drag-grip')) return;
      const grip = window.document.createElement('button');
      grip.type = 'button';
      grip.className = 'owdex-chat-drag-grip';
      grip.setAttribute('aria-label', 'Arrastar suporte OWDEX Chat');
      grip.setAttribute('title', 'Arraste para mover');
      grip.textContent = '⋮⋮';
      shell.appendChild(grip);
      makeDraggable(shell, grip);
    }

    function compactWidgetFrame(frame: HTMLIFrameElement) {
      if (frame.dataset.owdexCompactedFrame === 'true') return;
      const src = frame.getAttribute('src') ?? '';
      if (!/chat\.owdex\.com\.br/i.test(src)) return;
      const rect = frame.getBoundingClientRect();
      // Só compacta o launcher pequeno. A janela do chat aberta permanece intacta.
      if (rect.height > 120 || rect.width > 360 || rect.width < 70) return;
      let shell: HTMLElement = frame;
      for (let depth = 0, cursor = frame.parentElement; cursor && depth < 7; depth += 1, cursor = cursor.parentElement) {
        const style = getComputedStyle(cursor);
        if (style.position === 'fixed') { shell = cursor; break; }
      }
      frame.dataset.owdexCompactedFrame = 'true';
      shell.classList.add('owdex-chat-compact-host', 'owdex-chat-frame-host');
      important(shell, 'width', '58px'); important(shell, 'height', '58px');
      important(shell, 'min-width', '58px'); important(shell, 'max-width', '58px');
      important(shell, 'min-height', '58px'); important(shell, 'max-height', '58px');
      important(shell, 'overflow', 'hidden'); important(shell, 'border-radius', '50%'); important(shell, 'z-index', '17500');
      important(frame, 'width', `${Math.max(180, rect.width)}px`);
      important(frame, 'height', `${Math.max(54, rect.height)}px`);
      important(frame, 'min-width', `${Math.max(180, rect.width)}px`);
      important(frame, 'max-width', 'none');
      important(frame, 'transform', 'none'); important(frame, 'margin', '0');
      ensureDragGrip(shell);
    }

    function compactLauncher(launcher: HTMLElement) {
      if (launcher.dataset.owdexCompacted === 'true') return;
      launcher.dataset.owdexCompacted = 'true';
      launcher.classList.add('owdex-chat-compact-launcher');
      launcher.setAttribute('aria-label', 'Suporte OWDEX Chat');
      launcher.setAttribute('title', 'Suporte OWDEX Chat — arraste para mover');
      important(launcher, 'width', '54px'); important(launcher, 'height', '54px');
      important(launcher, 'min-width', '54px'); important(launcher, 'max-width', '54px');
      important(launcher, 'min-height', '54px'); important(launcher, 'max-height', '54px');
      important(launcher, 'padding', '0'); important(launcher, 'border-radius', '50%');
      important(launcher, 'font-size', '0'); important(launcher, 'line-height', '0');
      important(launcher, 'overflow', 'hidden'); important(launcher, 'display', 'grid');
      important(launcher, 'place-items', 'center'); important(launcher, 'cursor', 'grab');
      important(launcher, 'touch-action', 'none');
      for (const child of Array.from(launcher.querySelectorAll<HTMLElement>('span,strong,small,b,p'))) {
        const childText = child.textContent?.replace(/\s+/g, ' ').trim() ?? '';
        if (/fale conosco|falar conosco|suporte/i.test(childText)) important(child, 'display', 'none');
      }
      for (const icon of Array.from(launcher.querySelectorAll<HTMLElement>('svg,i,[class*="icon"]'))) {
        important(icon, 'display', 'block'); important(icon, 'font-size', '20px'); important(icon, 'line-height', '1');
      }
      let shell: HTMLElement = launcher;
      let cursor = launcher.parentElement;
      for (let depth = 0; cursor && depth < 7; depth += 1, cursor = cursor.parentElement) {
        const style = getComputedStyle(cursor);
        if (style.position === 'fixed') { shell = cursor; break; }
      }
      shell.classList.add('owdex-chat-compact-host');
      important(shell, 'width', '58px'); important(shell, 'height', '58px');
      important(shell, 'min-width', '58px'); important(shell, 'max-width', '58px');
      important(shell, 'min-height', '58px'); important(shell, 'max-height', '58px');
      important(shell, 'overflow', 'visible'); important(shell, 'z-index', '17500');
      makeDraggable(shell, launcher);
    }

    let scheduled = false;
    const normalizeWidget = () => {
      scheduled = false;
      const roots = rootsFrom(window.document);
      for (const root of roots) {
        const candidates = Array.from(root.querySelectorAll<HTMLElement>('button,a,[role="button"]'));
        for (const launcher of candidates) {
          const text = launcher.textContent?.replace(/\s+/g, ' ').trim() ?? '';
          const label = `${text} ${launcher.getAttribute('aria-label') ?? ''} ${launcher.getAttribute('title') ?? ''}`.trim();
          if (!/fale conosco|falar conosco/i.test(label)) continue;
          compactLauncher(launcher);
        }
        for (const frame of Array.from(root.querySelectorAll<HTMLIFrameElement>('iframe[src*="chat.owdex.com.br"],iframe[src*="/public/widget/"]'))) compactWidgetFrame(frame);
      }
    };
    const schedule = () => {
      if (scheduled) return; scheduled = true;
      window.requestAnimationFrame(normalizeWidget);
    };
    const observer = new MutationObserver(schedule);
    observer.observe(window.document.body, { childList: true, subtree: true, attributes: true });
    schedule();
    const timer = window.setInterval(schedule, 900);
    return () => { observer.disconnect(); window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    if (currentPublicShareToken()) { setAuthChecking(false); return; }
    if (!productionLoginRequired()) { setAuthChecking(false); return; }
    let cancelled = false;
    let stored: ServerSession | null = null;
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (raw) stored = JSON.parse(raw) as ServerSession;
    } catch { stored = null; }
    if (!stored?.token || !stored.expiresAt || new Date(stored.expiresAt).getTime() <= Date.now()) {
      try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* não crítico */ }
      setPersistenceScope(null);
      setAuthChecking(false); return;
    }
    const api = new ServerApi(serverBaseUrl);
    Promise.all([api.me(stored.token), api.listProjects(stored.token), api.listAnnouncements(stored.token).catch(() => [])])
      .then(([profile, projects, notices]) => {
        if (cancelled) return;
        setServerSession({ ...stored!, user: profile.user });
        setServerOrganizations(profile.organizations);
        setSyncOrganizationId(profile.organizations[0]?.id ?? '');
        setRemoteProjects(projects);
        setAnnouncements(notices);
      })
      .catch(() => {
        if (cancelled) return;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          setServerSession(stored);
          setSyncError('Servidor indisponível: sessão mantida em modo local até a conexão voltar.');
          return;
        }
        try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* não crítico */ }
        setPersistenceScope(null);
      })
      .finally(() => { if (!cancelled) setAuthChecking(false); });
    return () => { cancelled = true; };
  }, [serverBaseUrl]);

  useEffect(() => {
    if (!serverSession) { setAccountInfo(null); setNotifications([]); setUnreadNotifications(0); return; }
    let cancelled = false;
    const api = new ServerApi(serverBaseUrl);
    const refresh = async () => {
      try {
        const [account, noticeResult] = await Promise.all([api.account(serverSession.token), api.listNotifications(serverSession.token)]);
        if (cancelled) return;
        setAccountInfo(account);
        setNotifications(noticeResult.notifications);
        setUnreadNotifications(noticeResult.unread);
      } catch { /* conta/notificações não bloqueiam o editor */ }
    };
    void refresh();
    const timer = window.setInterval(() => { if (typeof navigator === 'undefined' || navigator.onLine !== false) void refresh(); }, 30_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [serverBaseUrl, serverSession]);

  useEffect(() => {
    if (!presentationMode) return;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' || event.key === 'F10') { event.preventDefault(); setPresentationMode(false); } };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [presentationMode]);

  const capabilities = useMemo(() => detectDeviceCapabilities(), []);
  const document = history.present;
  const page = getActivePage(document);
  const selectedNode = selection.nodeIds.length === 1 ? page.nodes.find((node) => node.id === selection.nodeIds[0]) ?? null : null;
  const selectedEdge = selection.edgeIds.length === 1 ? page.edges.find((edge) => edge.id === selection.edgeIds[0]) ?? null : null;
  const selectedEdgeIsBpmn = Boolean(selectedEdge && [selectedEdge.sourceId, selectedEdge.targetId].every((id) => page.nodes.some((node) => node.id === id && ['bpmnTask','bpmnSubprocess','bpmnDataObject','bpmnEvent','bpmnBoundaryEvent','bpmnTextAnnotation','bpmnGateway','bpmnPool','bpmnLane'].includes(node.type))));
  const selectedNodeList = useMemo(() => selectedNodes(page, selection), [page, selection]);
  const validationIssues = useMemo(() => validateDocumentSemantics(document), [document]);
  const currentRemoteProject = remoteProjects.find((project) => project.id === document.id) ?? null;
  const currentRemotePermission = currentRemoteProject?.permission ?? null;
  const currentProjectOrganizationRole = currentRemoteProject?.organization_role ?? (serverOrganizations.find((org) => org.id === currentRemoteProject?.organization_id)?.role ?? null);
  const canEditCurrent = currentRemotePermission === null || ['owner','edit'].includes(currentRemotePermission);

  const refreshLocalProjects = useCallback(async (ownerScope = serverSession?.user.id) => {
    if (productionLoginRequired() && !ownerScope) { setLocalProjects([]); return []; }
    try {
      const documents = await listLocalDocuments(ownerScope);
      setLocalProjects(documents);
      return documents;
    } catch {
      setLocalProjects([]);
      return [];
    }
  }, [serverSession?.user.id]);

  const dashboardProjects = useMemo<RemoteProjectSummary[]>(() => {
    const organizationName = serverOrganizations.find((org) => org.id === syncOrganizationId)?.name ?? serverOrganizations[0]?.name ?? 'OWDEX';
    const remoteById = new Map(remoteProjects.map((project) => [project.id, project]));
    const localById = new Map(localProjects.map((project) => [project.id, project]));
    const merged: RemoteProjectSummary[] = remoteProjects.map((project) => {
      const local = localById.get(project.id);
      if (!local || local.updatedAt <= project.updated_at) return { ...project, syncState: 'cloud' as const };
      return { ...project, title: local.title, updated_at: local.updatedAt, syncState: 'pending' as const };
    });
    for (const local of localProjects) {
      if (remoteById.has(local.id)) continue;
      merged.push({
        id: local.id, title: local.title || 'Diagrama sem título', revision: '',
        created_at: local.createdAt, updated_at: local.updatedAt, owner_user_id: serverSession?.user.id ?? 'local',
        organization_name: organizationName, organization_id: syncOrganizationId || serverOrganizations[0]?.id || null, organization_role: serverOrganizations.find((org) => org.id === syncOrganizationId)?.role ?? serverOrganizations[0]?.role ?? null, permission: 'owner', favorite: false, folder: '', tags: [],
        localOnly: true, syncState: 'local',
      });
    }
    return merged.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  }, [localProjects, remoteProjects, serverOrganizations, serverSession?.user.id, syncOrganizationId]);

  const tryLiveSync = useCallback((previous: DiagramDocument, next: DiagramDocument) => {
    if (!serverSession || !navigator.onLine) return;
    const remote = remoteProjects.find((project) => project.id === next.id);
    if (!remote || !['owner','edit'].includes(remote.permission)) return;
    const operations = deriveLiveOperations(previous, next);
    if (!operations) return;
    liveSyncChain.current = liveSyncChain.current.then(async () => {
      try {
        const result = await new ServerApi(serverBaseUrl).applyLiveOperations(serverSession.token, next.id, next.updatedAt, remote.revision, operations);
        await markDocumentSynced(next.id, next.updatedAt, result.revision, serverSession.user.id);
        setRemoteProjects((current) => current.map((item) => item.id === next.id ? { ...item, revision: result.revision, updated_at: new Date().toISOString() } : item));
        if (result.concurrent) setRemoteUpdateAvailable(true);
        setPendingSyncCount(await countPendingSync(serverSession?.user.id));
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message.includes('Conflito de operação')) {
          setSyncConflicts((current) => current.includes(next.id) ? current : [...current, next.id]);
          setRemoteUpdateAvailable(true);
        }
        // Em qualquer falha a outbox documental continua sendo o fallback seguro.
      }
    });
  }, [remoteProjects, serverBaseUrl, serverSession]);

  const commitDocument = useCallback((next: DiagramDocument) => {
    if (!canEditCurrent) return;
    const stamped = { ...next, updatedAt: new Date().toISOString() };
    const ownerScope = serverSession?.user.id;
    saveEmergencySnapshot(stamped, ownerScope);
    setHistory((current) => commit(current, stamped));
    setSaveStatus('saving');
    tryLiveSync(document, stamped);
  }, [canEditCurrent, document, serverSession?.user.id, tryLiveSync]);

  function finishInteraction(base: DiagramDocument | null) {
    interactionBase.current = null;
    if (!base) { setSaveStatus('saving'); return; }
    const stamped = { ...history.present, updatedAt: new Date().toISOString() };
    const ownerScope = serverSession?.user.id;
    saveEmergencySnapshot(stamped, ownerScope);
    setHistory((current) => ({ ...current, past: [...current.past.slice(-49), base], present: { ...current.present, updatedAt: stamped.updatedAt }, future: [] }));
    setSaveStatus('saving');
    tryLiveSync(base, stamped);
  }

  useEffect(() => {
    if (currentPublicShareToken()) return;
    const production = productionLoginRequired();
    if (production && (authChecking || !serverSession)) { setLoaded(false); return; }
    const ownerScope = serverSession?.user.id;
    const storageScope = ownerScope ? `user:${ownerScope}` : 'local';
    setPersistenceScope(storageScope);
    let cancelled = false;
    setLoaded(false);
    void (async () => {
      const stored = await loadLatestLocal(ownerScope);
      if (cancelled) return;
      if (stored?.schemaVersion === 1) {
        const recovered = await loadRecoverySession(stored.id);
        if (cancelled) return;
        const useRecovery = recovered && recovered.present.updatedAt >= stored.updatedAt;
        setHistory(useRecovery ? recovered : createHistory(stored));
      } else {
        setHistory(createHistory(createWelcomeDocument()));
      }
      setSelection(EMPTY_SELECTION);
      setViewport(INITIAL_VIEWPORT);
      setConnectionMode(false);
      setConnectionSourceId(null);
      setConnectionSourceAnchor('auto');
      setLoaded(true);
      void refreshLocalProjects(ownerScope);
      void countPendingSync(ownerScope).then(setPendingSyncCount).catch(() => undefined);
    })();
    return () => { cancelled = true; };
  }, [authChecking, refreshLocalProjects, serverSession?.user.id]);

  useEffect(() => {
    // A Home mantém um documento em memória apenas para "Continuar editando".
    // Enquanto ela estiver aberta, esse documento NÃO pode ser persistido/colocado na outbox:
    // isso evitava que o placeholder de bootstrap virasse um "Diagrama sem título" fantasma.
    if (!loaded || dashboardOpen) return;
    const ownerScope = serverSession?.user.id;
    if (productionLoginRequired() && !ownerScope) return;

    // Snapshot síncrono antes de qualquer debounce: protege contra refresh, travamento ou fechamento imediato.
    saveEmergencySnapshot(history.present, ownerScope);
    setSaveStatus('saving');

    const timer = window.setTimeout(() => {
      void Promise.all([saveLocal(history.present, ownerScope), saveRecoverySession(history)])
        .then(async () => {
          setLocalProjects((current) => [history.present, ...current.filter((item) => item.id !== history.present.id)].sort((a,b) => b.updatedAt.localeCompare(a.updatedAt)));
          setSaveStatus('saved');
          try {
            const remoteSync = remoteAppliedSync.current;
            if (remoteSync && remoteSync.id === history.present.id && remoteSync.updatedAt === history.present.updatedAt) {
              await markDocumentSynced(remoteSync.id, remoteSync.updatedAt, remoteSync.revision, ownerScope);
              remoteAppliedSync.current = null;
            } else {
              const existsInCloud = remoteProjects.some((item) => item.id === history.present.id);
              await queueDocumentForSync(history.present, ownerScope, existsInCloud ? undefined : { force: true, baseRevision: null });
            }
            setPendingSyncCount(await countPendingSync(ownerScope));
          } catch {
            // A edição local continua funcional mesmo se a outbox futura estiver indisponível.
          }
        })
        .catch(() => setSaveStatus('error'));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [dashboardOpen, history, loaded, remoteProjects, serverSession?.user.id]);

  useEffect(() => {
    // Pelo mesmo motivo do autosave acima, pagehide/visibility da Home não deve salvar um placeholder.
    if (!loaded || dashboardOpen) return;
    const ownerScope = serverSession?.user.id;
    const flushRecovery = () => {
      saveEmergencySnapshot(history.present, ownerScope);
      void saveRecoverySession(history);
      void saveLocal(history.present, ownerScope);
    };
    const onVisibility = () => { if (globalThis.document.visibilityState === 'hidden') flushRecovery(); };
    globalThis.document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flushRecovery);
    window.addEventListener('beforeunload', flushRecovery);
    return () => {
      globalThis.document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushRecovery);
      window.removeEventListener('beforeunload', flushRecovery);
    };
  }, [dashboardOpen, history, loaded, serverSession?.user.id]);

  useEffect(() => {
    saveEditorPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    try { localStorage.setItem('owdex-draw:server-url', serverBaseUrl); } catch { /* preferência opcional */ }
  }, [serverBaseUrl]);

  const deleteSelected = useCallback(() => {
    if (selection.nodeIds.length === 0 && selection.edgeIds.length === 0) return;
    const selectedNodesSet = new Set(selection.nodeIds);
    const selectedEdgesSet = new Set(selection.edgeIds);
    const removable = new Set(page.nodes.filter((node) => selectedNodesSet.has(node.id) && !node.locked).map((node) => node.id));
    const next = updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.filter((node) => !removable.has(node.id)),
      edges: current.edges.filter((edge) => !selectedEdgesSet.has(edge.id) && !removable.has(edge.sourceId) && !removable.has(edge.targetId)),
    }));
    if (removable.size === 0 && selectedEdgesSet.size === 0) return;
    commitDocument(next);
    setSelection(EMPTY_SELECTION);
  }, [commitDocument, document, page.nodes, selection]);

  const copySelected = useCallback(() => {
    const nodeIds = new Set(selection.nodeIds);
    clipboard.current = {
      nodes: page.nodes.filter((node) => nodeIds.has(node.id)).map((node) => structuredClone(node)),
      edges: page.edges.filter((edge) => nodeIds.has(edge.sourceId) && nodeIds.has(edge.targetId)).map((edge) => structuredClone(edge)),
    };
  }, [page.edges, page.nodes, selection.nodeIds]);

  const cutSelected = useCallback(() => {
    if (selection.nodeIds.length === 0 && selection.edgeIds.length === 0) return;
    copySelected();
    deleteSelected();
  }, [copySelected, deleteSelected, selection.edgeIds.length, selection.nodeIds.length]);

  const pasteClipboard = useCallback(() => {
    if (clipboard.current.nodes.length === 0) return;
    const idMap = new Map<string, string>();
    const groupMap = new Map<string, string>();
    const clones = clipboard.current.nodes.map((node) => {
      const id = crypto.randomUUID();
      idMap.set(node.id, id);
      let groupId = node.groupId ?? null;
      if (groupId) {
        if (!groupMap.has(groupId)) groupMap.set(groupId, crypto.randomUUID());
        groupId = groupMap.get(groupId)!;
      }
      return { ...structuredClone(node), id, groupId, x: node.x + 30, y: node.y + 30 };
    });
    const clonedEdges = clipboard.current.edges.map((edge) => ({
      ...structuredClone(edge),
      id: crypto.randomUUID(),
      sourceId: idMap.get(edge.sourceId)!,
      targetId: idMap.get(edge.targetId)!,
    }));
    commitDocument(updateActivePage(history.present, (current) => ({ ...current, nodes: [...current.nodes, ...clones], edges: [...current.edges, ...clonedEdges] })));
    setSelection({ nodeIds: clones.map((node) => node.id), edgeIds: [] });
  }, [commitDocument, history.present]);

  const duplicateSelected = useCallback(() => {
    if (selection.nodeIds.length === 0) return;
    copySelected();
    pasteClipboard();
  }, [copySelected, pasteClipboard, selection.nodeIds.length]);

  const groupSelected = useCallback(() => {
    if (selection.nodeIds.length < 2) return;
    const groupId = crypto.randomUUID();
    const ids = new Set(selection.nodeIds);
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => ids.has(node.id) ? { ...node, groupId } : node),
    })));
  }, [commitDocument, document, selection.nodeIds]);

  const ungroupSelected = useCallback(() => {
    if (selection.nodeIds.length === 0) return;
    const selectedGroupIds = new Set(page.nodes.filter((node) => selection.nodeIds.includes(node.id) && node.groupId).map((node) => node.groupId!));
    if (selectedGroupIds.size === 0) return;
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => node.groupId && selectedGroupIds.has(node.groupId) ? { ...node, groupId: null } : node),
    })));
  }, [commitDocument, document, page.nodes, selection.nodeIds]);


  const selectConnectedEdges = useCallback(() => {
    const ids = new Set(selection.nodeIds);
    if (ids.size === 0) return;
    const edgeIds = page.edges.filter((edge) => ids.has(edge.sourceId) || ids.has(edge.targetId)).map((edge) => edge.id);
    setSelection({ nodeIds: [], edgeIds });
  }, [page.edges, selection.nodeIds]);

  const connectSelectedNodes = useCallback(() => {
    const nodes = page.nodes.filter((node) => selection.nodeIds.includes(node.id) && !node.hidden);
    if (nodes.length < 2) return;
    const minX=Math.min(...nodes.map((node)=>node.x)), maxX=Math.max(...nodes.map((node)=>node.x+node.width));
    const minY=Math.min(...nodes.map((node)=>node.y)), maxY=Math.max(...nodes.map((node)=>node.y+node.height));
    const horizontal=(maxX-minX)>=(maxY-minY);
    const ordered=[...nodes].sort((a,b)=>horizontal ? (a.x+a.width/2)-(b.x+b.width/2) : (a.y+a.height/2)-(b.y+b.height/2));
    const existing=new Set(page.edges.map((edge)=>`${edge.sourceId}->${edge.targetId}`));
    const additions: DiagramEdge[]=[];
    for(let index=0;index<ordered.length-1;index+=1){
      const source=ordered[index],target=ordered[index+1]; const key=`${source.id}->${target.id}`;
      if(existing.has(key)) continue; const edge=createEdge(source.id,target.id); additions.push(edge); existing.add(key);
    }
    if(!additions.length) return;
    commitDocument(updateActivePage(document,(current)=>({...current,edges:[...current.edges,...additions]})));
    setSelection({nodeIds:selection.nodeIds,edgeIds:additions.map((edge)=>edge.id)});
  },[commitDocument,document,page.edges,page.nodes,selection.nodeIds]);

  const copySelectedStyle = useCallback(() => {
    if (selection.nodeIds.length !== 1) return;
    const source = page.nodes.find((node) => node.id === selection.nodeIds[0]);
    if (!source) return;
    styleClipboard.current = { ...source.style };
    setStyleClipboardReady(true);
  }, [page.nodes, selection.nodeIds]);

  const applyCopiedStyle = useCallback(() => {
    const style = styleClipboard.current;
    if (!style || selection.nodeIds.length === 0 || !canEditCurrent) return;
    const ids = new Set(selection.nodeIds);
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => ids.has(node.id) && !node.locked ? { ...node, style: { ...style } } : node),
    })));
  }, [canEditCurrent, commitDocument, document, selection.nodeIds]);

  const insertNextStep = useCallback(() => {
    if (!canEditCurrent || selection.nodeIds.length !== 1) return;
    const source = page.nodes.find((node) => node.id === selection.nodeIds[0]);
    if (!source || source.locked) return;
    const step = createNode('rectangle', source.x + source.width + 120, source.y);
    step.text = 'Novo passo';
    step.style = { ...source.style };
    for (let attempts = 0; attempts < 8; attempts += 1) {
      const collides = page.nodes.some((node) => node.id !== source.id && !(step.x + step.width + 20 < node.x || node.x + node.width + 20 < step.x || step.y + step.height + 20 < node.y || node.y + node.height + 20 < step.y));
      if (!collides) break;
      step.y += step.height + 48;
    }
    const edge = createEdge(source.id, step.id);
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, step], edges: [...current.edges, edge] })));
    setSelection({ nodeIds: [step.id], edgeIds: [] });
  }, [canEditCurrent, commitDocument, document, page.nodes, selection.nodeIds]);

  const smartDeleteInfo = useMemo(() => {
    if (selection.nodeIds.length !== 1) return null;
    const nodeId = selection.nodeIds[0];
    const incoming = page.edges.filter((edge) => edge.targetId === nodeId);
    const outgoing = page.edges.filter((edge) => edge.sourceId === nodeId);
    if (incoming.length !== 1 || outgoing.length !== 1 || incoming[0].sourceId === outgoing[0].targetId) return null;
    return { nodeId, incoming: incoming[0], outgoing: outgoing[0] };
  }, [page.edges, selection.nodeIds]);

  const deleteAndReconnect = useCallback(() => {
    if (!smartDeleteInfo || !canEditCurrent) return;
    const { nodeId, incoming, outgoing } = smartDeleteInfo;
    const existing = page.edges.some((edge) => edge.sourceId === incoming.sourceId && edge.targetId === outgoing.targetId && edge.id !== incoming.id && edge.id !== outgoing.id);
    const bridge: DiagramEdge = {
      ...outgoing, id: crypto.randomUUID(), sourceId: incoming.sourceId, targetId: outgoing.targetId,
      sourceAnchor: incoming.sourceAnchor ?? 'auto', targetAnchor: outgoing.targetAnchor ?? 'auto',
      startMarker: incoming.startMarker ?? (incoming.arrowStart ? 'arrow' : 'none'), arrowStart: incoming.arrowStart ?? false,
      bundleId: null, routingMode: 'auto', waypoints: [],
    };
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.filter((node) => node.id !== nodeId),
      edges: [...current.edges.filter((edge) => edge.sourceId !== nodeId && edge.targetId !== nodeId), ...(existing ? [] : [bridge])],
    })));
    setSelection(EMPTY_SELECTION);
  }, [canEditCurrent, commitDocument, document, page.edges, smartDeleteInfo]);

  const selectedEdges = useMemo(() => page.edges.filter((edge)=>selection.edgeIds.includes(edge.id)), [page.edges,selection.edgeIds]);
  const bundleMode = useMemo(() => {
    if(selectedEdges.length>=2){
      const sourceId=selectedEdges[0].sourceId;
      if(selectedEdges.every((edge)=>edge.sourceId===sourceId)) return { kind:'source' as const,id:sourceId,edges:selectedEdges };
      const targetId=selectedEdges[0].targetId;
      if(selectedEdges.every((edge)=>edge.targetId===targetId)) return { kind:'target' as const,id:targetId,edges:selectedEdges };
      return null;
    }
    if(selectedEdges.length===1){
      const edge=selectedEdges[0];
      const sameSource=page.edges.filter((item)=>item.sourceId===edge.sourceId);
      const sameTarget=page.edges.filter((item)=>item.targetId===edge.targetId);
      const best=sameSource.length>=sameTarget.length ? {kind:'source' as const,id:edge.sourceId,edges:sameSource} : {kind:'target' as const,id:edge.targetId,edges:sameTarget};
      return best.edges.length>=2 ? best : null;
    }
    return null;
  },[page.edges,selectedEdges]);
  const canBundleEdges=Boolean(bundleMode && bundleMode.edges.length>=2);
  const canUnbundleEdges=selectedEdges.some((edge)=>Boolean(edge.bundleId));

  const bundleSelectedEdges = useCallback(() => {
    if(!bundleMode) return;
    const edgesToBundle=bundleMode.edges;
    const nodeById=new Map(page.nodes.map((node)=>[node.id,node])); const common=nodeById.get(bundleMode.id); if(!common) return;
    const others=edgesToBundle.map((edge)=>nodeById.get(bundleMode.kind==='source'?edge.targetId:edge.sourceId)).filter((node): node is DiagramNode=>Boolean(node));
    if(others.length!==edgesToBundle.length) return;
    const average={x:others.reduce((sum,node)=>sum+node.x+node.width/2,0)/others.length,y:others.reduce((sum,node)=>sum+node.y+node.height/2,0)/others.length};
    const center={x:common.x+common.width/2,y:common.y+common.height/2}; const dx=average.x-center.x,dy=average.y-center.y;
    const side: Exclude<AnchorSide,'auto'>=Math.abs(dx)>=Math.abs(dy)?(dx>=0?'right':'left'):(dy>=0?'bottom':'top');
    const shared=anchorPoint(common,side); const direction=side==='right'?{x:1,y:0}:side==='left'?{x:-1,y:0}:side==='bottom'?{x:0,y:1}:{x:0,y:-1};
    const junction={x:shared.x+direction.x*72,y:shared.y+direction.y*72}; const bundleId=crypto.randomUUID(); const ids=new Set(edgesToBundle.map((edge)=>edge.id));
    commitDocument(updateActivePage(document,(current)=>({...current,edges:current.edges.map((edge)=>{
      if(!ids.has(edge.id)) return edge; const other=nodeById.get(bundleMode.kind==='source'?edge.targetId:edge.sourceId); if(!other) return edge;
      const otherCenter={x:other.x+other.width/2,y:other.y+other.height/2}; const odx=center.x-otherCenter.x,ody=center.y-otherCenter.y;
      const otherSide: Exclude<AnchorSide,'auto'>=Math.abs(odx)>=Math.abs(ody)?(odx>=0?'right':'left'):(ody>=0?'bottom':'top');
      const end=anchorPoint(other,otherSide);
      if(bundleMode.kind==='source'){
        const branch=Math.abs(direction.x)>0?{x:junction.x,y:end.y}:{x:end.x,y:junction.y};
        return {...edge,bundleId,routingMode:'manual' as const,sourceAnchor:side,targetAnchor:otherSide,waypoints:[junction,branch]};
      }
      const branch=Math.abs(direction.x)>0?{x:junction.x,y:end.y}:{x:end.x,y:junction.y};
      return {...edge,bundleId,routingMode:'manual' as const,targetAnchor:side,sourceAnchor:otherSide,waypoints:[branch,junction]};
    })})));
    setSelection({nodeIds:[],edgeIds:edgesToBundle.map((edge)=>edge.id)});
  },[bundleMode,commitDocument,document,page.nodes]);

  const unbundleSelectedEdges = useCallback(() => {
    const bundleIds=new Set(selectedEdges.map((edge)=>edge.bundleId).filter((value): value is string=>Boolean(value))); if(!bundleIds.size) return;
    commitDocument(updateActivePage(document,(current)=>({...current,edges:current.edges.map((edge)=>edge.bundleId&&bundleIds.has(edge.bundleId)?{...edge,bundleId:null,routingMode:'auto' as const,waypoints:[]}:edge)})));
  },[commitDocument,document,selectedEdges]);

  const reverseSelectedEdge = useCallback(() => {
    if (!selectedEdge || !canEditCurrent) return;
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      edges: current.edges.map((edge) => edge.id !== selectedEdge.id ? edge : ({
        ...edge,
        sourceId: edge.targetId,
        targetId: edge.sourceId,
        sourceAnchor: edge.targetAnchor,
        targetAnchor: edge.sourceAnchor,
        sourceCardinality: edge.targetCardinality,
        targetCardinality: edge.sourceCardinality,
        arrowStart: edge.arrowEnd,
        arrowEnd: edge.arrowStart ?? false,
        startMarker: edge.endMarker ?? (edge.arrowEnd ? 'arrow' : 'none'),
        endMarker: edge.startMarker ?? (edge.arrowStart ? 'arrow' : 'none'),
        waypoints: edge.waypoints ? [...edge.waypoints].reverse() : edge.waypoints,
      })),
    })));
  }, [canEditCurrent, commitDocument, document, selectedEdge]);

  const toggleLockSelected = useCallback(() => {
    if (selection.nodeIds.length === 0) return;
    const ids = new Set(selection.nodeIds);
    const nodes = page.nodes.filter((node) => ids.has(node.id));
    const shouldLock = nodes.some((node) => !node.locked);
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => ids.has(node.id) ? { ...node, locked: shouldLock } : node),
    })));
  }, [commitDocument, document, page.nodes, selection.nodeIds]);

  const moveLayer = useCallback((direction: 'front' | 'back') => {
    if (selection.nodeIds.length === 0) return;
    const ids = new Set(selection.nodeIds);
    commitDocument(updateActivePage(document, (current) => {
      const picked = current.nodes.filter((node) => ids.has(node.id));
      const rest = current.nodes.filter((node) => !ids.has(node.id));
      return { ...current, nodes: direction === 'front' ? [...rest, ...picked] : [...picked, ...rest] };
    }));
  }, [commitDocument, document, selection.nodeIds]);

  const alignSelected = useCallback((mode: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    const nodes = selectedNodes(page, selection).filter((node) => !node.locked);
    if (nodes.length < 2) return;
    const left = Math.min(...nodes.map((node) => node.x));
    const right = Math.max(...nodes.map((node) => node.x + node.width));
    const top = Math.min(...nodes.map((node) => node.y));
    const bottom = Math.max(...nodes.map((node) => node.y + node.height));
    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 2;
    const ids = new Set(nodes.map((node) => node.id));
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => {
        if (!ids.has(node.id)) return node;
        if (mode === 'left') return { ...node, x: left };
        if (mode === 'right') return { ...node, x: right - node.width };
        if (mode === 'center') return { ...node, x: centerX - node.width / 2 };
        if (mode === 'top') return { ...node, y: top };
        if (mode === 'bottom') return { ...node, y: bottom - node.height };
        return { ...node, y: centerY - node.height / 2 };
      }),
    })));
  }, [commitDocument, document, page, selection]);

  const distributeSelected = useCallback((axis: 'horizontal' | 'vertical') => {
    const nodes = selectedNodes(page, selection).filter((node) => !node.locked);
    if (nodes.length < 3) return;
    const sorted = [...nodes].sort((a, b) => axis === 'horizontal' ? a.x - b.x : a.y - b.y);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const firstCenter = axis === 'horizontal' ? first.x + first.width / 2 : first.y + first.height / 2;
    const lastCenter = axis === 'horizontal' ? last.x + last.width / 2 : last.y + last.height / 2;
    const step = (lastCenter - firstCenter) / (sorted.length - 1);
    const positions = new Map<string, number>();
    sorted.forEach((node, index) => positions.set(node.id, firstCenter + step * index));
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => {
        const center = positions.get(node.id);
        if (center === undefined) return node;
        return axis === 'horizontal' ? { ...node, x: center - node.width / 2 } : { ...node, y: center - node.height / 2 };
      }),
    })));
  }, [commitDocument, document, page, selection]);


  const matchSizeSelected = useCallback((mode: 'width' | 'height' | 'both') => {
    const nodes = selectedNodes(page, selection);
    if (nodes.length < 2) return;
    const reference = nodes[0];
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => {
        if (!selection.nodeIds.includes(node.id) || node.locked || node.id === reference.id) return node;
        if (mode === 'width') return { ...node, width: reference.width };
        if (mode === 'height') return { ...node, height: reference.height };
        return { ...node, width: reference.width, height: reference.height };
      }),
    })));
  }, [commitDocument, document, page, selection]);

  const syncDocumentNow = useCallback(async (documentToSync: DiagramDocument, ownerScope: string | undefined) => {
    if (!serverSession) return { ok: false, error: 'Sessão da OWDEX indisponível.', documentId: documentToSync.id };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ok: false, error: 'Sem conexão com a internet. A cópia local continua salva neste dispositivo.', documentId: documentToSync.id };
    try {
      let record = (await listSyncOutbox(ownerScope)).find((item) => item.documentId === documentToSync.id);
      const knownRemote = remoteProjects.find((item) => item.id === documentToSync.id);
      // Se a listagem remota não contém esse ID, a intenção é criar o projeto. Qualquer revisão
      // antiga no IndexedDB é inválida para esse fluxo e precisa ser zerada antes do PUT.
      if (!knownRemote && (!record || record.baseRevision !== null)) {
        await queueDocumentForSync(documentToSync, ownerScope, { force: true, baseRevision: null });
        record = (await listSyncOutbox(ownerScope)).find((item) => item.documentId === documentToSync.id);
      }
      if (!record) return { ok: true, documentId: documentToSync.id };
      const baseRevision = knownRemote ? (record.baseRevision ?? knownRemote.revision) : null;
      if (baseRevision !== record.baseRevision) {
        await queueDocumentForSync(record.document, ownerScope, { force: true, baseRevision });
        record = (await listSyncOutbox(ownerScope)).find((item) => item.documentId === documentToSync.id) ?? record;
      }
      const gateway = new SyncGateway({ baseUrl: serverBaseUrl, getAccessToken: async () => serverSession.token, getOrganizationId: () => syncOrganizationId || null });
      let pushed: Awaited<ReturnType<typeof pushSyncRecordWithRecovery>>;
      const syncRecord = record;
      try {
        pushed = await pushSyncRecordWithRecovery(gateway, syncRecord, ownerScope);
      } catch (error) {
        const trashedRemote = error instanceof SyncGatewayError && error.status === 410;
        if (!trashedRemote) throw error;
        const api = new ServerApi(serverBaseUrl);
        const trashedProjectId = syncRecord.documentId;
        await api.restoreProject(serverSession.token, trashedProjectId);
        const projects = await api.listProjects(serverSession.token);
        setRemoteProjects(projects);
        const restoredRemote = projects.find((item) => item.id === trashedProjectId);
        const retryBaseRevision = restoredRemote?.revision ?? syncRecord.baseRevision ?? null;
        await queueDocumentForSync(syncRecord.document, ownerScope, { force: true, baseRevision: retryBaseRevision });
        const retryRecord = (await listSyncOutbox(ownerScope)).find((item) => item.documentId === trashedProjectId) ?? { ...syncRecord, baseRevision: retryBaseRevision };
        pushed = await pushSyncRecordWithRecovery(gateway, retryRecord, ownerScope);
      }
      const syncedDocument = pushed.document;
      const now = new Date().toISOString();
      const activeOrganization = serverOrganizations.find((org) => org.id === syncOrganizationId) ?? serverOrganizations[0] ?? null;

      if (pushed.rekeyed) {
        // O ID local estava ocupado no servidor por um projeto invisível para esta sessão. A cópia
        // local recebeu uma identidade nova e foi salva sem tocar no projeto remoto conflitante.
        setHistory((current) => {
          const replaceId = (item: DiagramDocument) => item.id === pushed.sourceDocumentId ? { ...item, id: syncedDocument.id, updatedAt: syncedDocument.updatedAt } : item;
          return { past: current.past.map(replaceId), present: current.present.id === pushed.sourceDocumentId ? syncedDocument : current.present, future: current.future.map(replaceId) };
        });
        setLocalProjects((current) => [syncedDocument, ...current.filter((item) => item.id !== pushed.sourceDocumentId && item.id !== syncedDocument.id)].sort((a,b) => b.updatedAt.localeCompare(a.updatedAt)));
      }

      setRemoteProjects((current) => {
        const withoutLegacyId = pushed.rekeyed ? current.filter((item) => item.id !== pushed.sourceDocumentId) : current;
        const existing = withoutLegacyId.find((item) => item.id === syncedDocument.id);
        if (existing) return withoutLegacyId.map((item) => item.id === syncedDocument.id ? { ...item, revision: pushed.revision, title: syncedDocument.title, updated_at: now, localOnly: false, syncState: 'cloud' } : item);
        return [{
          id: syncedDocument.id, title: syncedDocument.title || 'Diagrama sem título', revision: pushed.revision,
          created_at: syncedDocument.createdAt, updated_at: now, owner_user_id: serverSession.user.id,
          organization_name: activeOrganization?.name ?? 'OWDEX', organization_id: activeOrganization?.id ?? null,
          organization_role: activeOrganization?.role ?? null, permission: 'owner', favorite: false, folder: '', tags: [],
          localOnly: false, syncState: 'cloud',
        }, ...withoutLegacyId];
      });
      try { setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token)); } catch { /* PUT confirmado; o resumo sintético mantém a UI correta. */ }
      setPendingSyncCount(await countPendingSync(ownerScope));
      setSyncError(null);
      return { ok: true, documentId: syncedDocument.id, rekeyedFrom: pushed.rekeyed ? pushed.sourceDocumentId : undefined };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao sincronizar o projeto com a OWDEX.';
      setSyncError(message);
      if (message.includes('Conflito de versão')) {
        setSyncConflicts((current) => current.includes(documentToSync.id) ? current : [...current, documentToSync.id]);
        setRemoteUpdateAvailable(true);
      }
      setPendingSyncCount(await countPendingSync(ownerScope));
      return { ok: false, error: message, documentId: documentToSync.id };
    }
  }, [remoteProjects, serverBaseUrl, serverOrganizations, serverSession, syncOrganizationId]);

  const saveCurrentNow = useCallback(async () => {
    if (!loaded || !canEditCurrent) return { ok: false, documentId: history.present.id, error: 'O projeto ainda não está pronto para salvar.' };
    const ownerScope = serverSession?.user.id;
    if (productionLoginRequired() && !ownerScope) return { ok: false, documentId: history.present.id, error: 'Sessão da OWDEX indisponível.' };
    setSaveStatus('saving');
    const stamped = { ...history.present, updatedAt: new Date().toISOString() };
    saveEmergencySnapshot(stamped, ownerScope);
    try {
      await Promise.all([saveLocal(stamped, ownerScope), saveRecoverySession({ ...history, present: stamped })]);
      setLocalProjects((current) => [stamped, ...current.filter((item) => item.id !== stamped.id)].sort((a,b) => b.updatedAt.localeCompare(a.updatedAt)));
      const existsInCloud = remoteProjects.some((item) => item.id === stamped.id);
      await queueDocumentForSync(stamped, ownerScope, existsInCloud ? undefined : { force: true, baseRevision: null });
      setHistory((current) => current.present.id === stamped.id ? { ...current, present: { ...current.present, updatedAt: stamped.updatedAt } } : current);
      const syncResult = await syncDocumentNow(stamped, ownerScope);
      if (!syncResult.ok && syncResult.error) setSyncError(syncResult.error);
      if (syncResult.ok && serverSession) {
        try { setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token)); } catch { /* PUT confirmado; o resumo sintético continua válido */ }
      }
      setPendingSyncCount(await countPendingSync(ownerScope));
      setSaveStatus('saved');
      return syncResult;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível salvar o projeto.';
      setSaveStatus('error');
      setSyncError(message);
      return { ok: false, documentId: stamped.id, error: message };
    }
  }, [canEditCurrent, history, loaded, remoteProjects, serverBaseUrl, serverSession, syncDocumentNow]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const editing = target?.matches('input,textarea,select,[contenteditable="true"]');
      const modifier = event.ctrlKey || event.metaKey;
      const key = event.key.toLowerCase();
      if (modifier && key === 's') {
        event.preventDefault();
        if (!editing) void saveCurrentNow();
        return;
      }
      if (modifier && key === 'k' && !editing) {
        event.preventDefault();
        setCommandsOpen(true);
        return;
      }
      if (modifier && key === 'f' && !editing) {
        event.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (!modifier && event.key === '?' && !editing) {
        event.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (modifier && event.altKey && key === 'b' && !editing) {
        event.preventDefault();
        setLibraryTab('shapes');
        if (window.innerWidth <= 760) { setMobileLibraryOpen((value) => !value); setMobilePropertiesOpen(false); }
        else window.document.getElementById('library-panel')?.focus();
        return;
      }
      if (modifier && event.altKey && key === 'm' && !editing) {
        event.preventDefault();
        setLibraryTab('templates');
        if (window.innerWidth <= 760) { setMobileLibraryOpen(true); setMobilePropertiesOpen(false); }
        else window.document.getElementById('library-panel')?.focus();
        return;
      }
      if (modifier && event.altKey && key === 'p' && !editing) {
        event.preventDefault();
        setMobilePropertiesOpen((value) => !value);
        setMobileLibraryOpen(false);
        return;
      }
      if (event.key === 'F6' && !editing) {
        event.preventDefault();
        const regions = ['editor-topbar', 'diagram-editor', 'properties-panel'].map((id) => globalThis.document.getElementById(id)).filter((item): item is HTMLElement => Boolean(item));
        if (regions.length) {
          const current = regions.findIndex((item) => item === globalThis.document.activeElement || item.contains(globalThis.document.activeElement));
          regions[(current + 1 + regions.length) % regions.length].focus();
        }
        return;
      }
      if (editing) return;
      if (modifier && key === 'z') {
        event.preventDefault();
        setHistory((current) => event.shiftKey ? redo(current) : undo(current));
      } else if (modifier && key === 'y') {
        event.preventDefault();
        setHistory((current) => redo(current));
      } else if (modifier && key === 'a' && event.shiftKey) {
        event.preventDefault();
        setSelection({ nodeIds: [], edgeIds: page.edges.map((edge) => edge.id) });
      } else if (modifier && key === 'a') {
        event.preventDefault();
        setSelection({ nodeIds: page.nodes.map((node) => node.id), edgeIds: [] });
      } else if (modifier && key === 'c') {
        copySelected();
      } else if (modifier && key === 'x') {
        event.preventDefault();
        cutSelected();
      } else if (modifier && key === 'v') {
        event.preventDefault();
        pasteClipboard();
      } else if (modifier && key === 'd') {
        event.preventDefault();
        duplicateSelected();
      } else if (modifier && key === 'g' && event.shiftKey) {
        event.preventDefault();
        ungroupSelected();
      } else if (modifier && key === 'g') {
        event.preventDefault();
        groupSelected();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        deleteSelected();
      } else if (event.key === 'F10') {
        event.preventDefault();
        setPresentationMode(true);
        try { const root = globalThis.document?.documentElement; if (root?.requestFullscreen) void root.requestFullscreen().catch(() => undefined); } catch {}
      } else if (event.key === 'Escape') {
        setMobileLibraryOpen(false);
        setMobilePropertiesOpen(false);
        setConnectionMode(false);
        setConnectionSourceId(null);
        setConnectionSourceAnchor('auto');
        setSelection(EMPTY_SELECTION);
      } else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) && selection.nodeIds.length > 0) {
        event.preventDefault();
        const amount = event.shiftKey ? 10 : 1;
        const ids = new Set(selection.nodeIds);
        commitDocument(updateActivePage(history.present, (current) => ({
          ...current,
          nodes: current.nodes.map((node) => {
            if (!ids.has(node.id) || node.locked) return node;
            if (event.key === 'ArrowLeft') return { ...node, x: node.x - amount };
            if (event.key === 'ArrowRight') return { ...node, x: node.x + amount };
            if (event.key === 'ArrowUp') return { ...node, y: node.y - amount };
            return { ...node, y: node.y + amount };
          }),
        })));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [commitDocument, copySelected, cutSelected, deleteSelected, duplicateSelected, groupSelected, history.present, page.nodes, pasteClipboard, saveCurrentNow, selection.nodeIds, ungroupSelected]);


  function applyTemplate(templateId: string) {
    const template = DIAGRAM_TEMPLATES.find((item) => item.id === templateId);
    if (!template) return;
    const generated = template.create();
    const minX = Math.min(...generated.nodes.map((node) => node.x));
    const minY = Math.min(...generated.nodes.map((node) => node.y));
    const centerX = (520 - viewport.panX) / viewport.zoom;
    const centerY = (330 - viewport.panY) / viewport.zoom;
    const maxX = Math.max(...generated.nodes.map((node) => node.x + node.width));
    const maxY = Math.max(...generated.nodes.map((node) => node.y + node.height));
    const offsetX = centerX - (minX + maxX) / 2;
    const offsetY = centerY - (minY + maxY) / 2;
    const nodes = generated.nodes.map((node) => ({ ...node, x: node.x + offsetX, y: node.y + offsetY }));
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, ...nodes], edges: [...current.edges, ...generated.edges] })));
    setSelection({ nodeIds: nodes.map((node) => node.id), edgeIds: [] });
  }

  function addNode(type: NodeType) {
    const worldCenterX = (520 - viewport.panX) / viewport.zoom;
    const worldCenterY = (330 - viewport.panY) / viewport.zoom;
    const node = createNode(type, Math.max(20, worldCenterX), Math.max(20, worldCenterY));
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, node] })));
    setSelection({ nodeIds: [node.id], edgeIds: [] });
  }

  function dropShape(type: string, x: number, y: number) {
    if (!SHAPE_BY_TYPE.has(type as NodeType)) return;
    const node = createNode(type as NodeType, Math.max(-5000, x - 80), Math.max(-5000, y - 40));
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, node] })));
    setSelection({ nodeIds: [node.id], edgeIds: [] });
  }

  function selectNode(id: string | null, additive = false) {
    if (!id) {
      setSelection(EMPTY_SELECTION);
      return;
    }
    const node = page.nodes.find((item) => item.id === id);
    const groupIds = node?.groupId ? page.nodes.filter((item) => item.groupId === node.groupId).map((item) => item.id) : [id];
    setSelection((current) => {
      if (!additive) return { nodeIds: groupIds, edgeIds: [] };
      const allSelected = groupIds.every((groupId) => current.nodeIds.includes(groupId));
      return {
        nodeIds: allSelected ? current.nodeIds.filter((item) => !groupIds.includes(item)) : Array.from(new Set([...current.nodeIds, ...groupIds])),
        edgeIds: [],
      };
    });
  }

  function selectArea(ids: string[], additive = false) {
    setSelection((current) => ({ nodeIds: additive ? Array.from(new Set([...current.nodeIds, ...ids])) : ids, edgeIds: [] }));
  }

  function selectEdge(id: string | null, additive = false) {
    if (!id) {
      setSelection(EMPTY_SELECTION);
      return;
    }
    setSelection((current) => {
      if (!additive) return { nodeIds: [], edgeIds: [id] };
      return { nodeIds: [], edgeIds: current.edgeIds.includes(id) ? current.edgeIds.filter((item) => item !== id) : [...current.edgeIds, id] };
    });
  }

  function moveNodes(changes: Array<{ id: string; x: number; y: number }>, shouldCommit = false) {
    if (!canEditCurrent) return;
    if (shouldCommit) {
      finishInteraction(interactionBase.current);
      return;
    }
    const positionMap = new Map(changes.map((item) => [item.id, item]));
    if (!interactionBase.current) interactionBase.current = history.present;
    setHistory((current) => {
      const activeBefore = getActivePage(current.present);
      const deltas = new Map<string, { dx: number; dy: number }>();
      for (const [id, change] of positionMap) {
        const before = activeBefore.nodes.find((node) => node.id === id);
        if (before) deltas.set(id, { dx: change.x - before.x, dy: change.y - before.y });
      }
      const next = updateActivePage(current.present, (active) => {
        const nextNodes = active.nodes.map((node) => {
          const change = positionMap.get(node.id);
          if (change && !node.locked) return { ...node, x: change.x, y: change.y };
          if (node.type === 'bpmnBoundaryEvent' && !node.locked && typeof node.data.attachedToId === 'string') {
            const delta = deltas.get(node.data.attachedToId);
            if (delta) return { ...node, x: node.x + delta.dx, y: node.y + delta.dy };
          }
          return node;
        });
        return { ...active, nodes: nextNodes, edges: repairBundledEdgesAfterGeometry(active, nextNodes, active.edges) };
      });
      return { ...current, present: { ...next, updatedAt: new Date().toISOString() } };
    });
    setSaveStatus('saving');
  }

  function transformNodes(changes: Array<{ id: string; x: number; y: number; width: number; height: number }>, shouldCommit = false) {
    if (!canEditCurrent) return;
    if (shouldCommit) {
      finishInteraction(interactionBase.current);
      return;
    }
    if (!interactionBase.current) interactionBase.current = history.present;
    const map = new Map(changes.map((item) => [item.id, item]));
    setHistory((current) => {
      const next = updateActivePage(current.present, (active) => {
        const nextNodes = active.nodes.map((node) => {
          const change = map.get(node.id);
          if (!change || node.locked) return node;
          return { ...node, x: change.x, y: change.y, width: Math.max(24, change.width), height: Math.max(24, change.height) };
        });
        return { ...active, nodes: nextNodes, edges: repairBundledEdgesAfterGeometry(active, nextNodes, active.edges) };
      });
      return { ...current, present: { ...next, updatedAt: new Date().toISOString() } };
    });
    setSaveStatus('saving');
  }

  function resizeNode(id: string, width: number, height: number, shouldCommit = false) {
    if (!canEditCurrent) return;
    if (shouldCommit) {
      finishInteraction(interactionBase.current);
      return;
    }
    const currentNode = page.nodes.find((node) => node.id === id);
    if (currentNode?.locked) return;
    if (!interactionBase.current) interactionBase.current = history.present;
    setHistory((current) => {
      const next = updateActivePage(current.present, (active) => {
        const nextNodes = active.nodes.map((node) => node.id === id ? { ...node, width, height } : node);
        return { ...active, nodes: nextNodes, edges: repairBundledEdgesAfterGeometry(active, nextNodes, active.edges) };
      });
      return { ...current, present: { ...next, updatedAt: new Date().toISOString() } };
    });
    setSaveStatus('saving');
  }

  function rotateNode(id: string, rotation: number, shouldCommit = false) {
    if (!canEditCurrent) return;
    if (shouldCommit) {
      finishInteraction(interactionBase.current);
      return;
    }
    const currentNode = page.nodes.find((node) => node.id === id);
    if (currentNode?.locked) return;
    if (!interactionBase.current) interactionBase.current = history.present;
    setHistory((current) => { const next = patchDocumentNode(current.present, id, { rotation }); return { ...current, present: { ...next, updatedAt: new Date().toISOString() } }; });
    setSaveStatus('saving');
  }

  function patchSelectedNode(patch: Partial<DiagramNode>) {
    if (!selectedNode) return;
    commitDocument(patchDocumentNode(document, selectedNode.id, patch));
  }

  function patchSelectedEdge(patch: Partial<DiagramEdge>) {
    if (!selectedEdge) return;
    commitDocument(patchDocumentEdge(document, selectedEdge.id, patch));
  }

  function patchSelectedEdges(patch: Partial<DiagramEdge>) {
    if (selection.edgeIds.length < 2) return;
    const ids = new Set(selection.edgeIds);
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      edges: current.edges.map((edge) => ids.has(edge.id) ? { ...edge, ...patch } : edge),
    })));
  }

  function patchSelectedNodes(patch: Partial<DiagramNode>) {
    const ids = new Set(selection.nodeIds);
    if (ids.size < 2) return;
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => ids.has(node.id) && !node.locked ? { ...node, ...patch } : node),
      edges: repairBundledEdgesAfterGeometry(current, current.nodes.map((node) => ids.has(node.id) && !node.locked ? { ...node, ...patch } : node), current.edges),
    })));
  }

  function patchSelectionStyle(patch: Partial<NodeStyle>) {
    const ids = new Set(selection.nodeIds);
    if (ids.size === 0) return;
    commitDocument(updateActivePage(document, (current) => ({
      ...current,
      nodes: current.nodes.map((node) => ids.has(node.id) && !node.locked ? { ...node, style: { ...node.style, ...patch } } : node),
    })));
  }

  function editNodeText(id: string, text: string) {
    const currentNode = page.nodes.find((node) => node.id === id);
    if (!currentNode) return;
    const data = { ...currentNode.data };
    if (currentNode.type === 'orgPerson' || currentNode.type === 'orgExecutive') data.name = text;
    if (currentNode.type === 'orgDepartment' || currentNode.type === 'orgTeam') data.department = text;
    if (currentNode.type === 'devApi') data.route = data.route || '/api/recurso';
    if (currentNode.type === 'engineeringFormula') data.formula = text;
    commitDocument(patchDocumentNode(document, id, { text, data }));
  }

  function editEdgeLabel(id: string, label: string) {
    commitDocument(patchDocumentEdge(document, id, { label }));
  }

  function updateEdgeWaypoints(id: string, waypoints: Array<{ x: number; y: number }>, shouldCommit = false) {
    if (!canEditCurrent) return;
    if (shouldCommit) {
      finishInteraction(interactionBase.current);
      return;
    }
    if (!interactionBase.current) interactionBase.current = history.present;
    setHistory((current) => {
      const next = updateActivePage(current.present, (active) => {
      const edge = active.edges.find((item) => item.id === id);
      if (!edge?.bundleId) return { ...active, edges: active.edges.map((item) => item.id === id ? { ...item, routingMode: 'manual' as const, waypoints } : item) };
      const group = active.edges.filter((item) => item.bundleId === edge.bundleId);
      const mode = bundleModeForEdges(group);
      if (!mode) return { ...active, edges: active.edges.map((item) => item.id === id ? { ...item, routingMode: 'manual' as const, waypoints } : item) };
      const oldPoints = edge.waypoints ?? [];
      const commonIndex = mode.kind === 'source' ? 0 : Math.max(0, oldPoints.length - 1);
      const commonMoved = Boolean(waypoints[commonIndex] && oldPoints[commonIndex] && (Math.abs(waypoints[commonIndex].x - oldPoints[commonIndex].x) > 0.01 || Math.abs(waypoints[commonIndex].y - oldPoints[commonIndex].y) > 0.01));
      if (!commonMoved) {
        return { ...active, edges: active.edges.map((item) => item.id === id ? { ...item, routingMode: 'manual' as const, waypoints } : item) };
      }
      const commonNode = active.nodes.find((node) => node.id === mode.nodeId);
      if (!commonNode) return active;
      const side = inferBundleSide(mode, active.nodes);
      const commonAnchor = anchorPoint(commonNode, side);
      const vertical = isVerticalBundleSide(side);
      const requested = waypoints[commonIndex];
      const junction = vertical ? { x: commonAnchor.x, y: requested.y } : { x: requested.x, y: commonAnchor.y };
      const nodeById = new Map(active.nodes.map((node) => [node.id, node]));
      return { ...active, edges: active.edges.map((item) => {
        if (item.bundleId !== edge.bundleId) return item;
        const other = nodeById.get(mode.kind === 'source' ? item.targetId : item.sourceId);
        if (!other) return item;
        const otherSide = (mode.kind === 'source' ? item.targetAnchor : item.sourceAnchor) ?? 'auto';
        const resolvedOtherSide = otherSide === 'auto' ? chooseAnchor(other, commonNode) : otherSide;
        const otherAnchor = anchorPoint(other, resolvedOtherSide);
        const branch = vertical ? { x: otherAnchor.x, y: junction.y } : { x: junction.x, y: otherAnchor.y };
        return { ...item, routingMode: 'manual' as const, waypoints: mode.kind === 'source' ? [junction, branch] : [branch, junction] };
      }) };
      });
      return { ...current, present: { ...next, updatedAt: new Date().toISOString() } };
    });
    setSaveStatus('saving');
  }

  function handleConnectionNode(id: string, anchor: AnchorSide = 'auto', keepSource = false) {
    if (!canEditCurrent) return;
    if (!connectionSourceId) {
      setConnectionSourceId(id);
      setConnectionSourceAnchor(anchor);
      setSelection({ nodeIds: [id], edgeIds: [] });
      return;
    }
    if (connectionSourceId === id) {
      setConnectionSourceId(null);
      setConnectionSourceAnchor('auto');
      return;
    }
    const edge = createEdge(connectionSourceId, id);
    edge.sourceAnchor = connectionSourceAnchor;
    edge.targetAnchor = anchor;
    commitDocument(updateActivePage(document, (current) => ({ ...current, edges: [...current.edges, edge] })));
    if (keepSource) {
      setSelection({ nodeIds: [connectionSourceId], edgeIds: [] });
      setConnectionMode(true);
    } else {
      setConnectionSourceId(null);
      setConnectionSourceAnchor('auto');
      setConnectionMode(false);
      setSelection({ nodeIds: [], edgeIds: [edge.id] });
    }
  }

  async function importOrgChartCsv(text: string) {
    try {
      const imported = importOrgCsv(text);
      const centerX = (520 - viewport.panX) / viewport.zoom;
      const centerY = (250 - viewport.panY) / viewport.zoom;
      const positions = imported.nodes.length > 1 ? await autoLayout(imported.nodes, imported.edges, 'flow') : imported.nodes.map((node) => ({ id: node.id, x: centerX, y: centerY }));
      const positionMap = new Map(positions.map((item) => [item.id, item]));
      const minX = Math.min(...positions.map((item) => item.x));
      const minY = Math.min(...positions.map((item) => item.y));
      const nodes = imported.nodes.map((node) => {
        const position = positionMap.get(node.id);
        return { ...node, x: (position?.x ?? 0) - minX + centerX - 220, y: (position?.y ?? 0) - minY + centerY - 120 };
      });
      commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, ...nodes], edges: [...current.edges, ...imported.edges] })));
      setSelection({ nodeIds: nodes.map((node) => node.id), edgeIds: [] });
      if (imported.warnings.length > 0) {
        const details = imported.warnings.slice(0, 8).join('\n');
        const more = imported.warnings.length > 8 ? '\n…' : '';
        window.alert(`Organograma importado com ${imported.rowCount} pessoa(s).\n\nAtenção:\n${details}${more}`);
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Falha ao importar o organograma.');
    }
  }

  async function importSqlErd(text: string) {
    try {
      const imported = importSqlSchema(text);
      const centerX = (520 - viewport.panX) / viewport.zoom;
      const centerY = (260 - viewport.panY) / viewport.zoom;
      const mode = imported.edges.length > 0 ? 'flow' : 'compact';
      const positions = imported.nodes.length > 1 ? await autoLayout(imported.nodes, imported.edges, mode) : imported.nodes.map((node) => ({ id: node.id, x: centerX, y: centerY }));
      const positionMap = new Map(positions.map((item) => [item.id, item]));
      const minX = Math.min(...positions.map((item) => item.x));
      const minY = Math.min(...positions.map((item) => item.y));
      const nodes = imported.nodes.map((node) => {
        const position = positionMap.get(node.id);
        return { ...node, x: (position?.x ?? 0) - minX + centerX - 260, y: (position?.y ?? 0) - minY + centerY - 140 };
      });
      commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, ...nodes], edges: [...current.edges, ...imported.edges] })));
      setSelection({ nodeIds: nodes.map((node) => node.id), edgeIds: [] });
      if (imported.warnings.length > 0) {
        const details = imported.warnings.slice(0, 8).join('\n');
        const more = imported.warnings.length > 8 ? '\n…' : '';
        window.alert(`ERD gerado com ${imported.tableCount} tabela(s).\n\nAtenção:\n${details}${more}`);
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Falha ao gerar ERD a partir do SQL.');
    }
  }

  async function importNetworkInventoryCsv(text: string) {
    try {
      const imported = importNetworkCsv(text);
      const centerX = (520 - viewport.panX) / viewport.zoom;
      const centerY = (260 - viewport.panY) / viewport.zoom;
      const mode = imported.edges.length > 0 ? 'flow' : 'compact';
      const positions = imported.nodes.length > 1
        ? await autoLayout(imported.nodes, imported.edges, mode)
        : imported.nodes.map((node) => ({ id: node.id, x: centerX, y: centerY }));
      const positionMap = new Map(positions.map((item) => [item.id, item]));
      const minX = Math.min(...positions.map((item) => item.x));
      const minY = Math.min(...positions.map((item) => item.y));
      const nodes = imported.nodes.map((node) => {
        const position = positionMap.get(node.id);
        return { ...node, x: (position?.x ?? 0) - minX + centerX - 250, y: (position?.y ?? 0) - minY + centerY - 140 };
      });
      commitDocument(updateActivePage(document, (current) => ({
        ...current,
        nodes: [...current.nodes, ...nodes],
        edges: [...current.edges, ...imported.edges],
      })));
      setSelection({ nodeIds: nodes.map((node) => node.id), edgeIds: [] });
      if (imported.warnings.length > 0) {
        const details = imported.warnings.slice(0, 8).join('\n');
        const more = imported.warnings.length > 8 ? '\n…' : '';
        window.alert(`Rede importada com ${imported.rowCount} equipamento(s).\n\nAtenção:\n${details}${more}`);
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Falha ao importar o inventário de rede.');
    }
  }

  function importDataCsvToCurrentPage(text: string) {
    try {
      const result = bindDataCsv(page, text);
      commitDocument(updateActivePage(document, () => result.page));
      const suffix = result.unmatched.length ? `

Não encontrados (${result.unmatched.length}): ${result.unmatched.slice(0, 8).join(', ')}${result.unmatched.length > 8 ? '…' : ''}` : '';
      window.alert(`Dados aplicados em ${result.matched} objeto(s). Campos: ${result.fields.join(', ')}.${suffix}`);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao vincular os dados.'); }
  }

  async function importPlantUmlDiagram(text: string) {
    try {
      const imported = importPlantUml(text);
      const centerX = (520 - viewport.panX) / viewport.zoom;
      const centerY = (260 - viewport.panY) / viewport.zoom;
      const positions = imported.nodes.length > 1 ? await autoLayout(imported.nodes, imported.edges, imported.edges.length ? 'flow' : 'compact') : imported.nodes.map((node) => ({ id: node.id, x: centerX, y: centerY }));
      const positionMap = new Map(positions.map((item) => [item.id, item]));
      const minX = Math.min(...positions.map((item) => item.x)); const minY = Math.min(...positions.map((item) => item.y));
      const nodes = imported.nodes.map((node) => { const position = positionMap.get(node.id); return { ...node, x: (position?.x ?? 0) - minX + centerX - 260, y: (position?.y ?? 0) - minY + centerY - 140 }; });
      commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, ...nodes], edges: [...current.edges, ...imported.edges] })));
      setSelection({ nodeIds: nodes.map((node) => node.id), edgeIds: [] });
      if (imported.warnings.length) window.alert(`PlantUML importado com ${nodes.length} elemento(s).

Atenção:
${imported.warnings.slice(0, 8).join('\n')}${imported.warnings.length > 8 ? '\n…' : ''}`);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao importar PlantUML.'); }
  }



  async function importBpmnXmlDiagram(text: string) {
    try {
      const imported = importBpmnXml(text);
      const centerX=(520-viewport.panX)/viewport.zoom, centerY=(260-viewport.panY)/viewport.zoom;
      const flowNodes=imported.nodes.filter((node)=>!['bpmnPool','bpmnLane'].includes(node.type));
      const positions=flowNodes.length>1?await autoLayout(flowNodes, imported.edges.filter((edge)=>flowNodes.some((n)=>n.id===edge.sourceId)&&flowNodes.some((n)=>n.id===edge.targetId)), 'flow'):flowNodes.map((node)=>({id:node.id,x:centerX,y:centerY}));
      const posMap=new Map(positions.map((item)=>[item.id,item])); const minX=positions.length?Math.min(...positions.map((p)=>p.x)):0,minY=positions.length?Math.min(...positions.map((p)=>p.y)):0;
      const nodes=imported.nodes.map((node,index)=>{if(node.type==='bpmnPool')return {...node,x:centerX-320,y:centerY-220+index*20};if(node.type==='bpmnLane')return {...node,x:centerX-285,y:centerY-195+index*125};const pos=posMap.get(node.id);return {...node,x:(pos?.x??0)-minX+centerX-250,y:(pos?.y??0)-minY+centerY-140};});
      commitDocument(updateActivePage(document,(current)=>({...current,nodes:[...current.nodes,...nodes],edges:[...current.edges,...imported.edges]}))); setSelection({nodeIds:nodes.map((node)=>node.id),edgeIds:[]});
      if(imported.warnings.length)window.alert(`BPMN XML importado com ${nodes.length} elemento(s).\n\nAtenção:\n${imported.warnings.slice(0,8).join('\n')}${imported.warnings.length>8?'\n…':''}`);
    } catch(error){window.alert(error instanceof Error?error.message:'Falha ao importar BPMN XML.');}
  }

  async function importDotDiagram(text: string) {
    try {
      const imported = importDot(text);
      const centerX = (520 - viewport.panX) / viewport.zoom;
      const centerY = (260 - viewport.panY) / viewport.zoom;
      const positions = imported.nodes.length > 1 ? await autoLayout(imported.nodes, imported.edges, imported.suggestedLayout) : imported.nodes.map((node) => ({ id: node.id, x: centerX, y: centerY }));
      const positionMap = new Map(positions.map((item) => [item.id, item]));
      const minX = positions.length ? Math.min(...positions.map((item) => item.x)) : 0;
      const minY = positions.length ? Math.min(...positions.map((item) => item.y)) : 0;
      const nodes = imported.nodes.map((node) => { const position = positionMap.get(node.id); return { ...node, x: (position?.x ?? 0) - minX + centerX - 250, y: (position?.y ?? 0) - minY + centerY - 140 }; });
      commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, ...nodes], edges: [...current.edges, ...imported.edges] })));
      setSelection({ nodeIds: nodes.map((node) => node.id), edgeIds: [] });
      if (imported.warnings.length) window.alert(`DOT importado com ${nodes.length} objeto(s).\n\nAtenção:\n${imported.warnings.slice(0, 8).join('\n')}${imported.warnings.length > 8 ? '\n…' : ''}`);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao importar Graphviz DOT.'); }
  }

  async function importCloudInventoryCsv(text: string) {
    try {
      const imported = importCloudCsv(text);
      const centerX = (520 - viewport.panX) / viewport.zoom; const centerY = (260 - viewport.panY) / viewport.zoom;
      const positions = imported.nodes.length > 1 ? await autoLayout(imported.nodes, imported.edges, 'flow') : imported.nodes.map((node) => ({ id: node.id, x: centerX, y: centerY }));
      const map = new Map(positions.map((item) => [item.id, item]));
      const minX = positions.length ? Math.min(...positions.map((item) => item.x)) : 0; const minY = positions.length ? Math.min(...positions.map((item) => item.y)) : 0;
      const nodes = imported.nodes.map((node) => { const pos = map.get(node.id); return { ...node, x: (pos?.x ?? 0) - minX + centerX - 260, y: (pos?.y ?? 0) - minY + centerY - 140 }; });
      commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, ...nodes], edges: [...current.edges, ...imported.edges] })));
      setSelection({ nodeIds: nodes.map((node) => node.id), edgeIds: [] });
      if (imported.warnings.length) window.alert(`Inventário Cloud importado com ${nodes.length} recurso(s).\n\nAtenção:\n${imported.warnings.slice(0, 8).join('\n')}`);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao importar inventário Cloud.'); }
  }

  async function importMermaidFlowchart(text: string) {
    try {
      const imported = importMermaid(text);
      const centerX = (520 - viewport.panX) / viewport.zoom;
      const centerY = (260 - viewport.panY) / viewport.zoom;
      const positions = imported.nodes.length > 1
        ? await autoLayout(imported.nodes, imported.edges, imported.suggestedLayout)
        : imported.nodes.map((node) => ({ id: node.id, x: centerX, y: centerY }));
      const positionMap = new Map(positions.map((item) => [item.id, item]));
      const minX = Math.min(...positions.map((item) => item.x));
      const minY = Math.min(...positions.map((item) => item.y));
      const nodes = imported.nodes.map((node) => {
        const position = positionMap.get(node.id);
        return { ...node, x: (position?.x ?? 0) - minX + centerX - 250, y: (position?.y ?? 0) - minY + centerY - 140 };
      });
      commitDocument(updateActivePage(document, (current) => ({
        ...current,
        nodes: [...current.nodes, ...nodes],
        edges: [...current.edges, ...imported.edges],
      })));
      setSelection({ nodeIds: nodes.map((node) => node.id), edgeIds: [] });
      if (imported.warnings.length > 0) {
        const details = imported.warnings.slice(0, 8).join('\n');
        const more = imported.warnings.length > 8 ? '\n…' : '';
        window.alert(`Mermaid importado com ${nodes.length} objeto(s) e ${imported.edges.length} conexão(ões).\n\nAtenção:\n${details}${more}`);
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Falha ao importar o Mermaid.');
    }
  }

  function addOrgChild(parentId: string) {
    const parent = page.nodes.find((node) => node.id === parentId);
    if (!parent || parent.locked) return;
    const outgoingCount = page.edges.filter((edge) => edge.sourceId === parentId).length;
    const child = createNode('orgPerson', parent.x + outgoingCount * 235 - Math.max(0, outgoingCount - 1) * 115, parent.y + parent.height + 150);
    child.text = 'Novo colaborador';
    child.data = { cargo: 'Novo cargo' };
    const edge = createEdge(parent.id, child.id);
    edge.sourceAnchor = 'bottom';
    edge.targetAnchor = 'top';
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, child], edges: [...current.edges, edge] })));
    setSelection({ nodeIds: [child.id], edgeIds: [] });
  }

  async function arrangeOrg() {
    const targets = page.nodes.filter((node) => (node.type === 'orgPerson' || node.type === 'orgDepartment') && !node.locked);
    if (targets.length < 2) return;
    const ids = new Set(targets.map((node) => node.id));
    const edges = page.edges.filter((edge) => ids.has(edge.sourceId) && ids.has(edge.targetId));
    try {
      const positions = await autoLayout(targets, edges, 'organization');
      const map = new Map(positions.map((position) => [position.id, position]));
      commitDocument(updateActivePage(document, (current) => ({
        ...current,
        nodes: current.nodes.map((node) => {
          const position = map.get(node.id);
          return position ? { ...node, x: position.x, y: position.y } : node;
        }),
      })));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Falha ao organizar o organograma.');
    }
  }

  async function arrange(direction: LayoutMode) {
    const requested = selection.nodeIds.length > 1 ? page.nodes.filter((node) => selection.nodeIds.includes(node.id)) : page.nodes;
    const targets = requested.filter((node) => !node.locked);
    if (targets.length < 2) return;
    try {
      const targetIds = new Set(targets.map((node) => node.id));
      const targetEdges = page.edges.filter((edge) => targetIds.has(edge.sourceId) && targetIds.has(edge.targetId));
      const positions = await autoLayout(targets, targetEdges, direction);
      const map = new Map(positions.map((position) => [position.id, position]));
      commitDocument(updateActivePage(document, (current) => ({
        ...current,
        nodes: current.nodes.map((node) => {
          const position = map.get(node.id);
          return position ? { ...node, x: position.x, y: position.y } : node;
        }),
      })));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Falha ao organizar o diagrama.');
    }
  }

  function fitContent() {
    const visible = page.nodes.filter((node) => !node.hidden);
    if (visible.length === 0) {
      setViewport(INITIAL_VIEWPORT);
      return;
    }
    const bounds = visible.map((node) => nodeBounds(node));
    const minX = Math.min(...bounds.map((bound) => bound.left));
    const minY = Math.min(...bounds.map((bound) => bound.top));
    const maxX = Math.max(...bounds.map((bound) => bound.right));
    const maxY = Math.max(...bounds.map((bound) => bound.bottom));
    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxY - minY);
    const zoom = Math.min(1.4, Math.max(0.3, Math.min(850 / width, 500 / height)));
    setViewport({ zoom, panX: 100 - minX * zoom, panY: 80 - minY * zoom });
  }

  function addPage() {
    const newPage: DiagramPage = { id: crypto.randomUUID(), name: `Página ${document.pages.length + 1}`, nodes: [], edges: [] };
    commitDocument({ ...document, pages: [...document.pages, newPage], activePageId: newPage.id, updatedAt: new Date().toISOString() });
    setSelection(EMPTY_SELECTION);
    setViewport(INITIAL_VIEWPORT);
  }

  function activatePage(id: string) {
    if (id === document.activePageId) return;
    setHistory((current) => ({ ...current, present: { ...current.present, activePageId: id } }));
    setSelection(EMPTY_SELECTION);
    setViewport(INITIAL_VIEWPORT);
  }

  function renamePage(id: string, name: string) {
    commitDocument({ ...document, pages: document.pages.map((item) => item.id === id ? { ...item, name } : item), updatedAt: new Date().toISOString() });
  }

  function duplicatePage(id: string) {
    const source = document.pages.find((item) => item.id === id);
    if (!source) return;
    const idMap = new Map<string, string>();
    const groupMap = new Map<string, string>();
    const pageCopyId = crypto.randomUUID();
    const nodes = source.nodes.map((node) => {
      const nextId = crypto.randomUUID();
      idMap.set(node.id, nextId);
      let groupId = node.groupId ?? null;
      if (groupId) {
        if (!groupMap.has(groupId)) groupMap.set(groupId, crypto.randomUUID());
        groupId = groupMap.get(groupId)!;
      }
      const cloned = structuredClone(node);
      const linkPageId = typeof cloned.data?.linkPageId === 'string' ? cloned.data.linkPageId : '';
      return { ...cloned, id: nextId, groupId, data: linkPageId === source.id ? { ...cloned.data, linkPageId: pageCopyId } : cloned.data };
    });
    const edges = source.edges.map((edge) => ({
      ...structuredClone(edge),
      id: crypto.randomUUID(),
      sourceId: idMap.get(edge.sourceId)!,
      targetId: idMap.get(edge.targetId)!,
    }));
    const pageCopy: DiagramPage = { id: pageCopyId, name: `${source.name} (cópia)`, nodes, edges };
    const sourceIndex = document.pages.findIndex((item) => item.id === id);
    const pages = [...document.pages];
    pages.splice(sourceIndex + 1, 0, pageCopy);
    commitDocument({ ...document, pages, activePageId: pageCopy.id, updatedAt: new Date().toISOString() });
    setSelection(EMPTY_SELECTION);
    setViewport(INITIAL_VIEWPORT);
  }

  function movePage(id: string, direction: 'left' | 'right') {
    const index = document.pages.findIndex((item) => item.id === id);
    if (index < 0) return;
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= document.pages.length) return;
    const pages = [...document.pages];
    const [moved] = pages.splice(index, 1);
    pages.splice(targetIndex, 0, moved);
    commitDocument({ ...document, pages, updatedAt: new Date().toISOString() });
  }

  function deletePage(id: string) {
    if (document.pages.length <= 1 || !canEditCurrent) return;
    const impact = analyzePageDeletion(document, id);
    if (!impact) return;
    const links = impact.incomingLinks.length > 0 ? `
${impact.incomingLinks.length} atalho(s) interno(s) serão limpos.` : '';
    const content = `
${impact.nodeCount} objeto(s) e ${impact.edgeCount} conexão(ões) serão removidos.`;
    if (!window.confirm(`Excluir a página “${impact.pageName}”?${content}${links}

A exclusão será sincronizada de forma segura e recusada se outro colaborador tiver alterado a página antes.`)) return;
    const next = removePageWithLinkCleanup(document, id, impact.fallbackPageId);
    if (next === document) return;
    commitDocument(next);
    setSelection(EMPTY_SELECTION);
    setViewport(INITIAL_VIEWPORT);
  }


  function normalizeBpmnSwimlanes() {
    if (!canEditCurrent) return;
    const normalized = normalizeBpmnMembership(page);
    commitDocument(updateActivePage(document, () => normalized));
  }

  function toggleNodeHidden(id: string) {
    const node = page.nodes.find((item) => item.id === id);
    if (!node) return;
    commitDocument(patchDocumentNode(document, id, { hidden: !node.hidden }));
  }

  function toggleNodeLockedById(id: string) {
    const node = page.nodes.find((item) => item.id === id);
    if (!node) return;
    commitDocument(patchDocumentNode(document, id, { locked: !node.locked }));
  }

  function moveLayerById(id: string, direction: 'front' | 'back') {
    commitDocument(updateActivePage(document, (current) => {
      const node = current.nodes.find((item) => item.id === id);
      if (!node) return current;
      const rest = current.nodes.filter((item) => item.id !== id);
      return { ...current, nodes: direction === 'front' ? [...rest, node] : [node, ...rest] };
    }));
  }

  function addMindChild(parentId: string) {
    const parent = page.nodes.find((node) => node.id === parentId);
    if (!parent || parent.locked || (parent.type !== 'mindRoot' && parent.type !== 'mindTopic')) return;
    const child = createNode('mindTopic', parent.x + parent.width + 230, parent.y);
    child.text = 'Novo tópico';
    const edge = createEdge(parent.id, child.id);
    edge.style = 'straight';
    edge.arrowEnd = false;
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, child], edges: [...current.edges, edge] })));
    setSelection({ nodeIds: [child.id], edgeIds: [] });
    window.setTimeout(() => void arrangeMind(), 0);
  }

  async function arrangeMind() {
    const targets = page.nodes.filter((node) => (node.type === 'mindRoot' || node.type === 'mindTopic') && !node.locked);
    if (targets.length < 2) return;
    const ids = new Set(targets.map((node) => node.id));
    const edges = page.edges.filter((edge) => ids.has(edge.sourceId) && ids.has(edge.targetId));
    try {
      const positions = await autoLayout(targets, edges, 'mindmap');
      const map = new Map(positions.map((position) => [position.id, position]));
      commitDocument(updateActivePage(document, (current) => ({
        ...current,
        nodes: current.nodes.map((node) => map.has(node.id) && !node.locked ? { ...node, ...map.get(node.id)! } : node),
      })));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Não foi possível organizar o mapa mental.');
    }
  }

  function selectValidationIssue(issue: DiagramIssue) {
    const targetPage = issue.pageId ? document.pages.find((item) => item.id === issue.pageId) : page;
    if (!targetPage) return;
    if (targetPage.id !== document.activePageId) setHistory((current) => ({ ...current, present: { ...current.present, activePageId: targetPage.id } }));
    if (issue.nodeId) {
      setSelection({ nodeIds: [issue.nodeId], edgeIds: [] });
      const node = targetPage.nodes.find((item) => item.id === issue.nodeId);
      if (node) setViewport((current) => ({ ...current, panX: 450 - (node.x + node.width / 2) * current.zoom, panY: 280 - (node.y + node.height / 2) * current.zoom }));
    } else if (issue.edgeId) {
      setSelection({ nodeIds: [], edgeIds: [issue.edgeId] });
    } else {
      setSelection(EMPTY_SELECTION);
    }
    setValidationOpen(false);
  }

  async function openProject(next: DiagramDocument) {
    await saveCurrentNow();
    setHistory(createHistory(next));
    setSelection(EMPTY_SELECTION);
    setViewport(INITIAL_VIEWPORT);
    setConnectionMode(false);
    setConnectionSourceId(null);
    setConnectionSourceAnchor('auto');
    setProjectsOpen(false);
    setDashboardOpen(false);
  }

  function newDocument() {
    if (!window.confirm('Criar um novo diagrama? O atual já está salvo localmente.')) return;
    setHistory(createHistory(createEmptyDocument()));
    setSelection(EMPTY_SELECTION);
    setViewport(INITIAL_VIEWPORT);
    setConnectionMode(false);
    setConnectionSourceId(null);
    setConnectionSourceAnchor('auto');
    setDashboardOpen(false);
  }

  function restoreHistorySnapshot(snapshot: DiagramDocument) {
    const restored = structuredClone(snapshot);
    restored.updatedAt = new Date().toISOString();
    setHistory((current) => ({
      past: [...current.past.slice(-49), current.present],
      present: restored,
      future: [],
    }));
    setSelection(EMPTY_SELECTION);
    setConnectionMode(false);
    setConnectionSourceId(null);
    setConnectionSourceAnchor('auto');
    setSaveStatus('saving');
    setHistoryOpen(false);
  }

  function openSearchResult(result: ProjectSearchResult) {
    const targetPage = document.pages.find((item) => item.id === result.pageId);
    if (!targetPage) return;
    setHistory((current) => ({ ...current, present: { ...current.present, activePageId: result.pageId } }));
    if (result.kind === 'node') {
      const node = targetPage.nodes.find((item) => item.id === result.targetId);
      if (!node) return;
      setSelection({ nodeIds: [node.id], edgeIds: [] });
      const zoom = Math.max(0.6, Math.min(1.35, viewport.zoom));
      setViewport({ zoom, panX: 520 - (node.x + node.width / 2) * zoom, panY: 300 - (node.y + node.height / 2) * zoom });
    } else {
      const edge = targetPage.edges.find((item) => item.id === result.targetId);
      if (!edge) return;
      setSelection({ nodeIds: [], edgeIds: [edge.id] });
      const source = targetPage.nodes.find((item) => item.id === edge.sourceId);
      const target = targetPage.nodes.find((item) => item.id === edge.targetId);
      if (source && target) {
        const centerX = (source.x + source.width / 2 + target.x + target.width / 2) / 2;
        const centerY = (source.y + source.height / 2 + target.y + target.height / 2) / 2;
        const zoom = Math.max(0.6, Math.min(1.35, viewport.zoom));
        setViewport({ zoom, panX: 520 - centerX * zoom, panY: 300 - centerY * zoom });
      }
    }
  }

  function runCommand(action: CommandAction) {
    if (action === 'fit') fitContent();
    else if (action === 'layers') setLayersOpen(true);
    else if (action === 'new-page') addPage();
    else if (action === 'arrange-flow') void arrange('flow');
    else if (action === 'arrange-horizontal') void arrange('horizontal');
    else if (action === 'arrange-vertical') void arrange('vertical');
    else if (action === 'arrange-tree') void arrange('tree');
    else if (action === 'arrange-radial') void arrange('radial');
    else if (action === 'arrange-compact') void arrange('compact');
    else if (action === 'connect') {
      setConnectionMode(true);
      setConnectionSourceId(null);
      setConnectionSourceAnchor('auto');
    }
  }

  const refreshAnnouncements = useCallback(async () => {
    if (!serverSession) { setAnnouncements([]); return; }
    try { setAnnouncements(await new ServerApi(serverBaseUrl).listAnnouncements(serverSession.token)); }
    catch { /* comunicado não deve bloquear o editor */ }
  }, [serverBaseUrl, serverSession]);

  const dismissAnnouncement = useCallback(async (announcement: Announcement) => {
    if (!serverSession || !announcement.dismissible || announcementBusy) return;
    setAnnouncementBusy(true);
    try {
      await new ServerApi(serverBaseUrl).dismissAnnouncement(serverSession.token, announcement.id);
      setAnnouncements((current) => current.filter((item) => item.id !== announcement.id));
    } finally { setAnnouncementBusy(false); }
  }, [announcementBusy, serverBaseUrl, serverSession]);

  const synchronizeNow = useCallback(async () => {
    if (!serverSession || syncBusy) return;
    const pendingBefore = await countPendingSync(serverSession.user.id);
    setPendingSyncCount(pendingBefore);
    if (pendingBefore === 0) return;
    setSyncBusy(true);
    setSyncError(null);
    try {
      const gateway = new SyncGateway({ baseUrl: serverBaseUrl, getAccessToken: async () => serverSession.token, getOrganizationId: () => syncOrganizationId || null });
      const result = await flushSyncOutbox(gateway, undefined, 25, serverSession.user.id);
      setSyncLastResult(result);
      setSyncConflicts(result.conflictDocumentIds);
      setPendingSyncCount(await countPendingSync(serverSession.user.id));
      if (result.syncedDocuments.length > 0) {
        const activeOrganization = serverOrganizations.find((org) => org.id === syncOrganizationId) ?? serverOrganizations[0] ?? null;
        if (result.rekeyedDocuments.length > 0) {
          setHistory((current) => {
            let next = current;
            for (const rekeyed of result.rekeyedDocuments) {
              const replaceId = (item: DiagramDocument) => item.id === rekeyed.fromId ? { ...item, id: rekeyed.document.id, updatedAt: rekeyed.document.updatedAt } : item;
              next = { past: next.past.map(replaceId), present: next.present.id === rekeyed.fromId ? rekeyed.document : next.present, future: next.future.map(replaceId) };
            }
            return next;
          });
        }
        setRemoteProjects((current) => {
          const byId = new Map(current.map((item) => [item.id, item]));
          for (const rekeyed of result.rekeyedDocuments) byId.delete(rekeyed.fromId);
          for (const synced of result.syncedDocuments) {
            const existing = byId.get(synced.document.id);
            byId.set(synced.document.id, existing ? { ...existing, title: synced.document.title, revision: synced.revision, updated_at: synced.document.updatedAt, localOnly: false, syncState: 'cloud' } : {
              id: synced.document.id, title: synced.document.title || 'Diagrama sem título', revision: synced.revision, created_at: synced.document.createdAt, updated_at: synced.document.updatedAt,
              owner_user_id: serverSession.user.id, organization_name: activeOrganization?.name ?? 'OWDEX', organization_id: activeOrganization?.id ?? null, organization_role: activeOrganization?.role ?? null,
              permission: 'owner', favorite: false, folder: '', tags: [], localOnly: false, syncState: 'cloud',
            });
          }
          return [...byId.values()].sort((a,b) => b.updated_at.localeCompare(a.updated_at));
        });
        await refreshLocalProjects(serverSession.user.id);
      }
      if (result.conflicts === 0 && result.failed === 0) setRemoteUpdateAvailable(false);
      try { setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token)); } catch { /* sucesso do PUT continua refletido pelos resumos locais acima */ }
      if (result.conflicts > 0) setSyncError(`${result.conflicts} conflito(s) de versão foram preservados para resolução; nada foi sobrescrito.`);
      else if (result.failed > 0) { const detail = result.failedErrors[0]?.message; setSyncError(`${result.failed} item(ns) não puderam ser sincronizados. O conteúdo local permanece salvo.${detail ? ` Motivo: ${detail}` : ''}`); }
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Falha ao sincronizar com o servidor OWDEX.');
    } finally {
      setSyncBusy(false);
    }
  }, [refreshLocalProjects, serverBaseUrl, serverOrganizations, serverSession, syncBusy, syncOrganizationId]);

  const loginServer = useCallback(async (email: string, password: string) => {
    if (syncBusy) return;
    setSyncBusy(true);
    setSyncError(null);
    try {
      const api = new ServerApi(serverBaseUrl);
      await api.health();
      const session = await api.login(email, password);
      const profile = await api.me(session.token);
      const projects = await api.listProjects(session.token);
      const hydratedSession = { ...session, user: profile.user };
      // Nunca reaproveitar o documento que estava em memória para outra identidade.
      setPersistenceScope(`user:${profile.user.id}`);
      setLoaded(false);
      setHistory(createHistory(createWelcomeDocument()));
      setSelection(EMPTY_SELECTION);
      setViewport(INITIAL_VIEWPORT);
      setConnectionMode(false);
      setConnectionSourceId(null);
      setConnectionSourceAnchor('auto');
      remoteAppliedSync.current = null;
      setServerSession(hydratedSession);
      try { sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(hydratedSession)); } catch { /* sessão continua em memória */ }
      setServerOrganizations(profile.organizations);
      setSyncOrganizationId((current) => profile.organizations.some((org) => org.id === current) ? current : (profile.organizations[0]?.id ?? ''));
      setRemoteProjects(projects);
      setSyncConflicts([]);
      setDashboardOpen(true);
      try { setAnnouncements(await api.listAnnouncements(session.token)); } catch { setAnnouncements([]); }
    } catch (error) {
      setServerSession(null);
      setPersistenceScope(null);
      setServerOrganizations([]);
      setSyncOrganizationId('');
      setSyncError(error instanceof Error ? error.message : 'Não foi possível entrar no servidor OWDEX.');
    } finally {
      setSyncBusy(false);
    }
  }, [serverBaseUrl, syncBusy]);

  const logoutServer = useCallback(async () => {
    if (!serverSession || syncBusy) return;
    setSyncBusy(true);
    setSyncError(null);
    try { await new ServerApi(serverBaseUrl).logout(serverSession.token); }
    catch { /* encerrar localmente mesmo se o servidor estiver indisponível */ }
    finally {
      setLoaded(false);
      setHistory(createHistory(createWelcomeDocument()));
      setSelection(EMPTY_SELECTION);
      setViewport(INITIAL_VIEWPORT);
      setConnectionMode(false);
      setConnectionSourceId(null);
      setConnectionSourceAnchor('auto');
      remoteAppliedSync.current = null;
      setPersistenceScope(null);
      setServerSession(null);
      try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* não crítico */ }
      setServerOrganizations([]);
      setSyncOrganizationId('');
      setSyncLastResult(null);
      setRemoteProjects([]);
      setAnnouncements([]);
      setNotifications([]); setUnreadNotifications(0); setAccountInfo(null);
      setSyncConflicts([]);
      setDashboardOpen(true); setAccountOpen(false); setNotificationOpen(false); setDashboardShareProject(null);
      setRealtimeUsers([]); setRealtimeConnected(false); setRemoteUpdateAvailable(false);
      setSyncBusy(false);
    }
  }, [serverBaseUrl, serverSession, syncBusy]);

  const refreshRemoteProjects = useCallback(async () => {
    if (!serverSession || syncBusy) return;
    setSyncBusy(true);
    setSyncError(null);
    try { setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token)); }
    catch (error) { setSyncError(error instanceof Error ? error.message : 'Falha ao listar projetos do servidor.'); }
    finally { setSyncBusy(false); }
  }, [serverBaseUrl, serverSession, syncBusy]);

  const openRemoteProject = useCallback(async (project: RemoteProjectSummary) => {
    if (!serverSession || syncBusy) return;
    setSyncBusy(true);
    setSyncError(null);
    try {
      const ownerScope = serverSession.user.id;
      const api = new ServerApi(serverBaseUrl);
      const [outbox, local, remote] = await Promise.all([
        listSyncOutbox(ownerScope),
        loadLocalDocument(project.id, ownerScope),
        api.getProject(serverSession.token, project.id),
      ]);
      const pending = outbox.find((item) => item.documentId === project.id);
      const localCandidate = pending?.document ?? local;
      const remoteCount = documentObjectCount(remote.document);
      const localCount = localCandidate ? documentObjectCount(localCandidate) : 0;
      const differs = Boolean(localCandidate && !documentsHaveSameContent(localCandidate, remote.document));

      // Nunca trocar silenciosamente um projeto local preenchido por uma versão remota vazia.
      // Esse cenário pode ocorrer quando o usuário voltou para a Home antes da sincronização terminar.
      if (localCandidate && differs && localCount > 0 && remoteCount === 0) {
        const gateway = new SyncGateway({ baseUrl: serverBaseUrl, getAccessToken: async () => serverSession.token, getOrganizationId: () => syncOrganizationId || null });
        try {
          const pushed = await gateway.push({ documentId: project.id, baseRevision: remote.revision, localRevision: localCandidate.updatedAt, document: localCandidate });
          await markDocumentSynced(project.id, localCandidate.updatedAt, pushed.acceptedRevision, ownerScope);
          await adoptRemoteDocument(localCandidate, pushed.acceptedRevision, ownerScope);
          setHistory(createHistory(localCandidate));
          setRemoteProjects(await api.listProjects(serverSession.token));
          setSyncConflicts((current) => current.filter((id) => id !== project.id));
          setRemoteUpdateAvailable(false);
          setPendingSyncCount(await countPendingSync(ownerScope));
          setSelection(EMPTY_SELECTION);
          setViewport(INITIAL_VIEWPORT);
          setConnectionMode(false);
          setConnectionSourceId(null);
          setConnectionSourceAnchor('auto');
          setServerPanelOpen(false);
          setDashboardOpen(false);
          return;
        } catch (error) {
          const message = error instanceof Error ? error.message : '';
          if (!message.includes('Conflito de versão')) throw error;
        }
      }

      if (localCandidate && differs && (pending || localCandidate.updatedAt > remote.document.updatedAt)) {
        // Há duas versões reais. O editor abre a cópia local e exige decisão explícita, sem destruir nenhuma delas.
        setHistory(createHistory(localCandidate));
        setSelection(EMPTY_SELECTION);
        setViewport(INITIAL_VIEWPORT);
        setConnectionMode(false);
        setConnectionSourceId(null);
        setConnectionSourceAnchor('auto');
        setRevisionCompare({ local: localCandidate, remote: remote.document, revision: remote.revision });
        setServerPanelOpen(false);
        setDashboardOpen(false);
        return;
      }

      if (pending && localCandidate && !differs) await markDocumentSynced(project.id, pending.localRevision, remote.revision, ownerScope);
      await adoptRemoteDocument(remote.document, remote.revision, ownerScope);
      setHistory(createHistory(remote.document));
      setSelection(EMPTY_SELECTION);
      setViewport(INITIAL_VIEWPORT);
      setConnectionMode(false);
      setConnectionSourceId(null);
      setConnectionSourceAnchor('auto');
      setSyncConflicts((current) => current.filter((id) => id !== project.id));
      setRemoteUpdateAvailable(false);
      setPendingSyncCount(await countPendingSync(ownerScope));
      setServerPanelOpen(false);
      setDashboardOpen(false);
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Falha ao abrir projeto do servidor.');
    } finally { setSyncBusy(false); }
  }, [serverBaseUrl, serverSession, syncBusy, syncOrganizationId]);

  const openRemoteCompareCopy = useCallback(async () => {
    if (!revisionCompare) return;
    const copy = duplicateDocument(revisionCompare.remote, `${revisionCompare.remote.title} (cópia do servidor)`);
    await saveLocal(copy, serverSession?.user.id); await queueDocumentForSync(copy, serverSession?.user.id);
    setHistory(createHistory(copy)); setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT);
    setRevisionCompare(null); setPendingSyncCount(await countPendingSync(serverSession?.user.id));
  }, [revisionCompare]);

  const keepComparedLocal = useCallback(async () => {
    if (!revisionCompare || !serverSession || syncBusy) return;
    setSyncBusy(true); setSyncError(null);
    try {
      // A decisão é explícita: preserva a versão remota como cópia antes de publicar a local.
      const remoteBackup = duplicateDocument(revisionCompare.remote, `${revisionCompare.remote.title} (backup remoto antes da substituição)`);
      await saveLocal(remoteBackup, serverSession.user.id);
      await queueDocumentForSync(remoteBackup, serverSession.user.id);
      const local = { ...revisionCompare.local, updatedAt: new Date().toISOString() };
      await saveLocal(local, serverSession.user.id);
      const gateway = new SyncGateway({ baseUrl: serverBaseUrl, getAccessToken: async () => serverSession.token, getOrganizationId: () => syncOrganizationId || null });
      const pushed = await gateway.push({ documentId: local.id, baseRevision: revisionCompare.revision, localRevision: local.updatedAt, document: local });
      await markDocumentSynced(local.id, local.updatedAt, pushed.acceptedRevision, serverSession.user.id);
      await adoptRemoteDocument(local, pushed.acceptedRevision, serverSession.user.id);
      setHistory(createHistory(local)); setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT);
      setRevisionCompare(null); setRemoteUpdateAvailable(false);
      setSyncConflicts((current) => current.filter((id) => id !== local.id));
      setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token));
      setPendingSyncCount(await countPendingSync(serverSession.user.id));
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Não foi possível manter a versão local no servidor.');
    } finally { setSyncBusy(false); }
  }, [revisionCompare, serverBaseUrl, serverSession, syncBusy, syncOrganizationId]);

  const adoptComparedRemoteSafely = useCallback(async () => {
    if (!revisionCompare) return;
    const backup = duplicateDocument(revisionCompare.local, `${revisionCompare.local.title} (cópia local antes do conflito)`);
    await saveLocal(backup, serverSession?.user.id); await queueDocumentForSync(backup, serverSession?.user.id);
    await adoptRemoteDocument(revisionCompare.remote, revisionCompare.revision, serverSession?.user.id);
    setHistory(createHistory(revisionCompare.remote)); setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT);
    setSyncConflicts((current) => current.filter((id) => id !== revisionCompare.remote.id));
    setRemoteUpdateAvailable(false); setRevisionCompare(null); setPendingSyncCount(await countPendingSync(serverSession?.user.id));
  }, [revisionCompare]);

  const reviewRemoteUpdate = useCallback(async () => {
    if (!serverSession || syncBusy) return;
    const project = remoteProjects.find((item) => item.id === document.id);
    if (!project) { setServerPanelOpen(true); return; }
    setSyncBusy(true); setSyncError(null);
    try {
      const remote = await new ServerApi(serverBaseUrl).getProject(serverSession.token, document.id);
      const outbox = await listSyncOutbox(serverSession?.user.id);
      const pending = outbox.find((item) => item.documentId === document.id);
      if (pending) setRevisionCompare({ local: pending.document, remote: remote.document, revision: remote.revision });
      else { await adoptRemoteDocument(remote.document, remote.revision, serverSession?.user.id); setHistory(createHistory(remote.document)); setSelection(EMPTY_SELECTION); setRemoteUpdateAvailable(false); }
    } catch (error) { setSyncError(error instanceof Error ? error.message : 'Falha ao revisar atualização remota.'); setServerPanelOpen(true); }
    finally { setSyncBusy(false); }
  }, [document.id, remoteProjects, serverBaseUrl, serverSession, syncBusy]);

  const compareServerRevision = useCallback(async (projectId: string, revision: ServerRevisionSummary) => {
    if (!serverSession || syncBusy) return;
    setSyncBusy(true); setSyncError(null);
    try {
      const historical = await new ServerApi(serverBaseUrl).getRevision(serverSession.token, projectId, revision.revision);
      setRevisionCompare({ local: document, remote: historical.document, revision: historical.revision });
      setServerPanelOpen(false);
    } catch (error) { setSyncError(error instanceof Error ? error.message : 'Falha ao comparar revisão histórica.'); }
    finally { setSyncBusy(false); }
  }, [document, serverBaseUrl, serverSession, syncBusy]);

  const openServerRevision = useCallback(async (projectId: string, revision: ServerRevisionSummary) => {
    if (!serverSession || syncBusy) return;
    setSyncBusy(true); setSyncError(null);
    try {
      const historical = await new ServerApi(serverBaseUrl).getRevision(serverSession.token, projectId, revision.revision);
      const copy = duplicateDocument(historical.document, `${historical.document.title} (revisão ${new Date(historical.createdAt).toLocaleString('pt-BR')})`);
      await saveLocal(copy, serverSession?.user.id);
      await queueDocumentForSync(copy, serverSession?.user.id);
      setHistory(createHistory(copy)); setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT);
      setPendingSyncCount(await countPendingSync(serverSession?.user.id));
      setServerPanelOpen(false);
    } catch (error) { setSyncError(error instanceof Error ? error.message : 'Falha ao abrir revisão histórica.'); }
    finally { setSyncBusy(false); }
  }, [serverBaseUrl, serverSession, syncBusy]);

  useEffect(() => {
    if (!serverSession) return;
    const onOnline = () => void synchronizeNow();
    window.addEventListener('online', onOnline);
    const timer = window.setInterval(() => { if (navigator.onLine) void synchronizeNow(); }, 5_000);
    return () => { window.removeEventListener('online', onOnline); window.clearInterval(timer); };
  }, [serverSession, synchronizeNow]);

  useEffect(() => {
    if (!serverSession) { setAnnouncements([]); return; }
    void refreshAnnouncements();
    const timer = window.setInterval(() => void refreshAnnouncements(), 5 * 60_000);
    return () => window.clearInterval(timer);
  }, [serverSession?.token, refreshAnnouncements]);

  useEffect(() => {
    if (!serverSession || !remoteProjects.some((project) => project.id === document.id)) { setCommentCounts({}); return; }
    let cancelled = false;
    void new ServerApi(serverBaseUrl).listComments(serverSession.token, document.id).then((comments) => {
      if (cancelled) return;
      const counts: Record<string, number> = {};
      for (const comment of comments) if (!comment.resolvedAt && comment.nodeId) counts[comment.nodeId] = (counts[comment.nodeId] ?? 0) + 1;
      setCommentCounts(counts);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [commentEventVersion, document.id, remoteProjects, serverBaseUrl, serverSession?.token]);

  useEffect(() => {
    realtimeClient.current?.stop(); realtimeClient.current = null;
    setRealtimeUsers([]); setRealtimeConnected(false); setRemoteUpdateAvailable(false);
    if (!serverSession || !remoteProjects.some((project) => project.id === document.id)) return;
    const client = new RealtimeProjectClient({
      baseUrl: serverBaseUrl,
      token: serverSession.token,
      projectId: document.id,
      onPresence: (users) => setRealtimeUsers(users.filter((user) => user.userId !== serverSession.user.id)),
      onConnection: setRealtimeConnected,
      onEvent: (event) => {
        if (event.type === 'project-updated' && event.data.userId !== serverSession.user.id) setRemoteUpdateAvailable(true);
        if (event.type === 'project-operations' && event.data.userId !== serverSession.user.id && isLiveOperationArray(event.data.operations) && typeof event.data.revision === 'string') {
          const operations = event.data.operations;
          setHistory((current) => {
            const result = applyRemoteLiveOperations(current.present, operations, typeof event.data.localRevision === 'string' ? event.data.localRevision : undefined);
            if (result.conflicts.length > 0) {
              setRemoteUpdateAvailable(true);
              return current;
            }
            if (result.applied === 0) return current;
            remoteAppliedSync.current = { id: current.present.id, updatedAt: result.document.updatedAt, revision: event.data.revision as string };
            setRemoteProjects((projects) => projects.map((project) => project.id === current.present.id ? { ...project, revision: event.data.revision as string, updated_at: new Date().toISOString() } : project));
            return commit(current, result.document);
          });
        }
        if (event.type === 'comment-changed') setCommentEventVersion((value) => value + 1);
        if (event.type === 'access-changed') void new ServerApi(serverBaseUrl).listProjects(serverSession.token).then(setRemoteProjects).catch(() => undefined);
      },
    });
    realtimeClient.current = client; client.start();
    return () => { client.stop(); if (realtimeClient.current === client) realtimeClient.current = null; };
  }, [document.id, remoteProjects.some((project) => project.id === document.id), serverBaseUrl, serverSession?.token]);

  useEffect(() => {
    realtimeClient.current?.setPresence(document.activePageId, selection.nodeIds);
  }, [document.activePageId, selection.nodeIds]);

  const saveNodeToLibrary = useCallback((node: DiagramNode) => {
    const suggested = node.text.trim().slice(0, 60) || SHAPE_BY_TYPE.get(node.type)?.label || 'Símbolo personalizado';
    const name = window.prompt('Nome do símbolo na sua biblioteca:', suggested);
    if (!name?.trim()) return;
    setCustomSymbols(saveCustomSymbol(name, node));
  }, []);

  const addCustomSymbol = useCallback((preset: CustomSymbolPreset) => {
    const x = Math.round((520 - viewport.panX) / viewport.zoom - preset.node.width / 2);
    const y = Math.round((340 - viewport.panY) / viewport.zoom - preset.node.height / 2);
    const base = createNode(preset.node.type, x, y);
    const node: DiagramNode = { ...structuredClone(preset.node), id: base.id, x, y, locked: false, hidden: false, groupId: null };
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, node] })));
    setSelection({ nodeIds: [node.id], edgeIds: [] });
  }, [commitDocument, document, viewport]);

  const saveCurrentPageAsTemplate = useCallback(() => {
    if (!canEditCurrent) return;
    const name = window.prompt('Nome do modelo:', page.name || 'Meu modelo');
    if (!name?.trim()) return;
    const description = window.prompt('Descrição curta do modelo (opcional):', '') ?? '';
    setCustomTemplates(saveCustomTemplate(name, description, page));
  }, [canEditCurrent, page]);

  const applyCustomTemplate = useCallback((preset: CustomTemplatePreset) => {
    if (!canEditCurrent) return;
    const targetX = Math.round((520 - viewport.panX) / viewport.zoom);
    const targetY = Math.round((340 - viewport.panY) / viewport.zoom);
    const created = instantiateCustomTemplate(preset, targetX, targetY);
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, ...created.nodes], edges: [...current.edges, ...created.edges] })));
    setSelection({ nodeIds: created.nodes.map((node) => node.id), edgeIds: created.edges.map((edge) => edge.id) });
  }, [canEditCurrent, commitDocument, document, viewport]);

  const refreshOrganizationTemplates = useCallback(async () => {
    if (!serverSession || !syncOrganizationId) { setOrganizationTemplates([]); return; }
    try { setOrganizationTemplates(await new ServerApi(serverBaseUrl).listOrganizationTemplates(serverSession.token, syncOrganizationId)); }
    catch (error) { setSyncError(error instanceof Error ? error.message : 'Falha ao carregar modelos da empresa.'); }
  }, [serverBaseUrl, serverSession, syncOrganizationId]);

  useEffect(() => { void refreshOrganizationTemplates(); }, [refreshOrganizationTemplates]);

  const saveOrganizationTemplate = useCallback(async () => {
    if (!serverSession || !syncOrganizationId || !canEditCurrent) { setServerPanelOpen(true); return; }
    const name = window.prompt('Nome do modelo compartilhado:', page.name || 'Modelo da empresa'); if (!name?.trim()) return;
    const description = window.prompt('Descrição curta (opcional):', '') ?? '';
    try {
      const created = await new ServerApi(serverBaseUrl).createOrganizationTemplate(serverSession.token, syncOrganizationId, { name, description, nodes: page.nodes, edges: page.edges });
      setOrganizationTemplates((current) => [created, ...current.filter((item) => item.id !== created.id)]);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível compartilhar o modelo.'); }
  }, [serverSession, syncOrganizationId, canEditCurrent, page, serverBaseUrl]);

  const applyOrganizationTemplate = useCallback((preset: OrganizationTemplate) => {
    if (!canEditCurrent) return;
    const targetX = Math.round((520 - viewport.panX) / viewport.zoom); const targetY = Math.round((340 - viewport.panY) / viewport.zoom);
    const localPreset: CustomTemplatePreset = { id: preset.id, name: preset.name, description: preset.description, createdAt: preset.createdAt, nodes: preset.template.nodes, edges: preset.template.edges };
    const created = instantiateCustomTemplate(localPreset, targetX, targetY);
    commitDocument(updateActivePage(document, (current) => ({ ...current, nodes: [...current.nodes, ...created.nodes], edges: [...current.edges, ...created.edges] })));
    setSelection({ nodeIds: created.nodes.map((node) => node.id), edgeIds: created.edges.map((edge) => edge.id) });
  }, [canEditCurrent, commitDocument, document, viewport]);

  const applyOrganizationTemplateAsPage = useCallback((preset: OrganizationTemplate) => {
    if (!canEditCurrent) return;
    const localPreset: CustomTemplatePreset = { id: preset.id, name: preset.name, description: preset.description, createdAt: preset.createdAt, nodes: preset.template.nodes, edges: preset.template.edges };
    const created = instantiateCustomTemplate(localPreset, 420, 300);
    const newPage: DiagramPage = { id: crypto.randomUUID(), name: preset.name.slice(0, 80) || `Página ${document.pages.length + 1}`, nodes: created.nodes, edges: created.edges };
    commitDocument({ ...document, pages: [...document.pages, newPage], activePageId: newPage.id, updatedAt: new Date().toISOString() });
    setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT);
  }, [canEditCurrent, commitDocument, document]);

  const updateOrganizationTemplate = useCallback(async (preset: OrganizationTemplate) => {
    if (!serverSession || !syncOrganizationId || !preset.canDelete || !canEditCurrent) return;
    if (!window.confirm(`Atualizar o modelo “${preset.name}” com o conteúdo da página atual?`)) return;
    const name = window.prompt('Nome do modelo:', preset.name)?.trim(); if (!name) return;
    const description = window.prompt('Descrição:', preset.description) ?? preset.description;
    try {
      const updated = await new ServerApi(serverBaseUrl).updateOrganizationTemplate(serverSession.token, syncOrganizationId, preset.id, { name, description, nodes: page.nodes, edges: page.edges });
      setOrganizationTemplates((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível atualizar o modelo compartilhado.'); }
  }, [serverSession, syncOrganizationId, canEditCurrent, serverBaseUrl, page]);

  const deleteOrganizationTemplate = useCallback(async (preset: OrganizationTemplate) => {
    if (!serverSession || !syncOrganizationId || !preset.canDelete || !window.confirm(`Excluir o modelo compartilhado “${preset.name}”?`)) return;
    try { await new ServerApi(serverBaseUrl).deleteOrganizationTemplate(serverSession.token, syncOrganizationId, preset.id); setOrganizationTemplates((current) => current.filter((item) => item.id !== preset.id)); }
    catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível excluir o modelo compartilhado.'); }
  }, [serverSession, syncOrganizationId, serverBaseUrl]);

  const exportLocalWorkspace = useCallback(async () => {
    try { downloadBlob(`OWDEX_DRAW_BACKUP_${new Date().toISOString().slice(0,10)}.json`, new Blob([await exportWorkspaceBackup()], { type: 'application/json' })); }
    catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao gerar backup local.'); }
  }, []);

  const restoreLocalWorkspace = useCallback(async (text: string) => {
    try {
      const result = await importWorkspaceBackup(text); setCustomSymbols(result.symbols); setCustomTemplates(result.templates);
      window.alert(`Backup restaurado: ${result.documents} projeto(s), ${result.symbols.length} símbolo(s) e ${result.templates.length} modelo(s).`);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao restaurar backup local.'); }
  }, []);

  const focusCollaborator = useCallback((user: RealtimePresenceUser) => {
    if (!user.pageId) return;
    const targetPage = document.pages.find((item) => item.id === user.pageId);
    if (!targetPage) return;
    if (document.activePageId !== user.pageId) {
      setHistory((current) => ({ ...current, present: { ...current.present, activePageId: user.pageId! } }));
      setSelection(EMPTY_SELECTION);
    }
    let point = user.cursor;
    if (!point && user.nodeIds[0]) {
      const node = targetPage.nodes.find((item) => item.id === user.nodeIds[0]);
      if (node) { const bounds = nodeBounds(node); point = { x: (bounds.left + bounds.right) / 2, y: (bounds.top + bounds.bottom) / 2 }; }
    }
    if (point) setViewport((current) => ({ ...current, panX: 520 - point!.x * current.zoom, panY: 360 - point!.y * current.zoom }));
    if (user.nodeIds.length) setSelection({ nodeIds: user.nodeIds.filter((id) => targetPage.nodes.some((node) => node.id === id)), edgeIds: [] });
    setCollaboratorsOpen(false);
  }, [document]);

  const openDashboardProject = useCallback(async (project: RemoteProjectSummary) => {
    if (!project.localOnly) { await openRemoteProject(project); return; }
    const local = await loadLocalDocument(project.id, serverSession?.user.id);
    if (!local) { window.alert('A cópia local deste projeto não foi encontrada. Atualize a tela e tente novamente.'); return; }
    await openProject(local);
  }, [openRemoteProject, serverSession?.user.id]);

  const updateProjectPreference = useCallback(async (project: RemoteProjectSummary, input: { favorite: boolean; folder: string; tags: string[] }) => {
    if (!serverSession || syncBusy) return;
    setSyncBusy(true); setSyncError(null);
    let remoteProject = project;
    try {
      // Favoritar/organizar um projeto local agora força a sincronização necessária em vez de bloquear o usuário.
      if (project.localOnly) {
        const local = await loadLocalDocument(project.id, serverSession.user.id);
        if (!local) throw new Error('A cópia local deste projeto não foi encontrada.');
        await saveLocal(local, serverSession.user.id);
        await queueDocumentForSync(local, serverSession.user.id, { force: true, baseRevision: null });
        const syncResult = await syncDocumentNow(local, serverSession.user.id);
        if (!syncResult.ok) throw new Error(syncResult.error || 'O projeto continua salvo neste dispositivo, mas ainda não foi possível enviá-lo para a OWDEX.');
        const effectiveProjectId = syncResult.documentId ?? project.id;
        remoteProject = { ...project, id: effectiveProjectId, localOnly: false, syncState: 'cloud' };
        try {
          const refreshed = await new ServerApi(serverBaseUrl).listProjects(serverSession.token);
          setRemoteProjects(refreshed);
          remoteProject = refreshed.find((item) => item.id === effectiveProjectId) ?? remoteProject;
        } catch {
          // O PUT do documento já foi confirmado; favoritar/organizar não deve depender de uma segunda listagem.
        }
      }
      setRemoteProjects((current) => current.map((item) => item.id === remoteProject.id ? { ...item, ...input, localOnly: false } : item));
      await new ServerApi(serverBaseUrl).updateProjectPreferences(serverSession.token, remoteProject.id, input);
    } catch (error) {
      try { setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token)); } catch { /* mantém o estado local atual */ }
      const message = error instanceof Error ? error.message : 'Não foi possível organizar o projeto.';
      setSyncError(message);
      window.alert(message);
    } finally {
      setPendingSyncCount(await countPendingSync(serverSession.user.id));
      await refreshLocalProjects(serverSession.user.id);
      setSyncBusy(false);
    }
  }, [refreshLocalProjects, serverBaseUrl, serverSession, syncBusy, syncDocumentNow]);

  const syncDashboardProject = useCallback(async (project: RemoteProjectSummary) => {
    if (!serverSession || syncBusy) return;
    setSyncBusy(true); setSyncError(null);
    try {
      const local = await loadLocalDocument(project.id, serverSession.user.id);
      if (!local) throw new Error('A cópia local deste projeto não foi encontrada.');
      await saveLocal(local, serverSession.user.id);
      await queueDocumentForSync(local, serverSession.user.id, { force: true, baseRevision: null });
      const syncResult = await syncDocumentNow(local, serverSession.user.id);
      if (!syncResult.ok) throw new Error(syncResult.error || 'Não foi possível salvar este projeto na OWDEX agora. A cópia deste dispositivo foi preservada.');
      try { setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token)); } catch { /* resumo sintético já foi aplicado */ }
      setPendingSyncCount(await countPendingSync(serverSession.user.id));
      await refreshLocalProjects(serverSession.user.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível salvar este projeto na OWDEX.';
      setSyncError(message); window.alert(message);
    } finally { setSyncBusy(false); }
  }, [refreshLocalProjects, serverBaseUrl, serverSession, syncBusy, syncDocumentNow]);

  const downloadDashboardProject = useCallback(async (project: RemoteProjectSummary) => {
    if (!serverSession || syncBusy) return;
    setSyncBusy(true); setSyncError(null);
    try {
      const local = project.localOnly ? await loadLocalDocument(project.id, serverSession.user.id) : null;
      const source = local ?? (await new ServerApi(serverBaseUrl).getProject(serverSession.token, project.id)).document;
      if (!source) throw new Error('Projeto não encontrado.');
      const safeName = (source.title || 'owdex_draw').replace(/[^a-z0-9_-]+/gi, '_').replace(/^_+|_+$/g, '') || 'owdex_draw';
      downloadBlob(`${safeName}.odxd`, new Blob([JSON.stringify(source, null, 2)], { type: 'application/vnd.owdex.draw+json' }));
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível salvar o projeto.'); }
    finally { setSyncBusy(false); }
  }, [serverBaseUrl, serverSession, syncBusy]);

  const duplicateDashboardProject = useCallback(async (project: RemoteProjectSummary, title: string) => {
    if (!serverSession || syncBusy || !title.trim()) return;
    setSyncBusy(true); setSyncError(null);
    try {
      const local = project.localOnly ? await loadLocalDocument(project.id, serverSession.user.id) : null;
      const source = local ?? (await new ServerApi(serverBaseUrl).getProject(serverSession.token, project.id)).document;
      if (!source) throw new Error('Projeto não encontrado.');
      const copy = duplicateDocument(source, title.trim().slice(0, 1000));
      await saveLocal(copy, serverSession?.user.id);
      const gateway = new SyncGateway({ baseUrl: serverBaseUrl, getAccessToken: async () => serverSession.token, getOrganizationId: () => syncOrganizationId || null });
      const pushed = await gateway.push({ documentId: copy.id, baseRevision: null, localRevision: copy.updatedAt, document: copy });
      await adoptRemoteDocument(copy, pushed.acceptedRevision, serverSession.user.id);
      setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token));
      setPendingSyncCount(await countPendingSync(serverSession.user.id));
      await refreshLocalProjects(serverSession.user.id);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível criar a cópia.'); }
    finally { setSyncBusy(false); }
  }, [refreshLocalProjects, serverBaseUrl, serverSession, syncBusy, syncOrganizationId]);

  const renameDashboardProject = useCallback(async (project: RemoteProjectSummary, title: string) => {
    if (!serverSession || syncBusy || project.permission !== 'owner' || !title.trim() || title.trim() === project.title) return;
    setSyncBusy(true); setSyncError(null);
    try {
      const api = new ServerApi(serverBaseUrl);
      const local = project.localOnly ? await loadLocalDocument(project.id, serverSession.user.id) : null;
      const remote = project.localOnly ? null : await api.getProject(serverSession.token, project.id);
      const source = local ?? remote?.document;
      if (!source) throw new Error('Projeto não encontrado.');
      const next = { ...source, title: title.trim().slice(0, 1000), updatedAt: new Date().toISOString() };
      await saveLocal(next, serverSession.user.id);
      await queueDocumentForSync(next, serverSession.user.id);
      const gateway = new SyncGateway({ baseUrl: serverBaseUrl, getAccessToken: async () => serverSession.token, getOrganizationId: () => syncOrganizationId || null });
      const pushed = await gateway.push({ documentId: project.id, baseRevision: remote?.revision ?? null, localRevision: next.updatedAt, document: next });
      await markDocumentSynced(project.id, next.updatedAt, pushed.acceptedRevision, serverSession.user.id);
      if (document.id === project.id) setHistory((current) => ({ ...current, present: { ...current.present, title: next.title, updatedAt: next.updatedAt } }));
      setRemoteProjects(await api.listProjects(serverSession.token));
      setPendingSyncCount(await countPendingSync(serverSession.user.id));
      await refreshLocalProjects(serverSession.user.id);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível renomear o projeto.'); }
    finally { setSyncBusy(false); }
  }, [document.id, refreshLocalProjects, serverBaseUrl, serverSession, syncBusy, syncOrganizationId]);

  const trashDashboardProject = useCallback(async (project: RemoteProjectSummary) => {
    if (!serverSession || syncBusy || project.permission !== 'owner') return;
    setSyncBusy(true); setSyncError(null);
    try {
      if (project.localOnly) {
        await deleteLocalDocument(project.id, serverSession.user.id);
        setLocalProjects((current) => current.filter((item) => item.id !== project.id));
      } else {
        await new ServerApi(serverBaseUrl).trashProject(serverSession.token, project.id);
        // A Home mescla nuvem + cache local. Sem limpar a cópia local, o mesmo projeto
        // reaparecia imediatamente como “Neste dispositivo” e dava a impressão de exigir
        // uma segunda exclusão. A lixeira remota continua preservando o projeto restaurável.
        await deleteLocalDocument(project.id, serverSession.user.id);
        setLocalProjects((current) => current.filter((item) => item.id !== project.id));
        setRemoteProjects((current) => current.filter((item) => item.id !== project.id));
        setPendingSyncCount(await countPendingSync(serverSession.user.id));
      }
      if (dashboardShareProject?.id === project.id) setDashboardShareProject(null);
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível excluir o projeto.'); }
    finally { setSyncBusy(false); }
  }, [dashboardShareProject?.id, serverBaseUrl, serverSession, syncBusy]);

  const startTemplateFromDashboard = useCallback((templateId: string) => {
    const template = DIAGRAM_TEMPLATES.find((item) => item.id === templateId); if (!template) return;
    const next = createEmptyDocument(); const generated = template.create();
    const minX = Math.min(...generated.nodes.map((node) => node.x), 0);
    const minY = Math.min(...generated.nodes.map((node) => node.y), 0);
    const nodes = generated.nodes.map((node) => ({ ...node, x: node.x - minX + 80, y: node.y - minY + 70 }));
    const page0 = next.pages[0];
    const created: DiagramDocument = { ...next, title: template.name, pages: [{ ...page0, name: template.name.slice(0, 80), nodes, edges: generated.edges }], updatedAt: new Date().toISOString() };
    setHistory(createHistory(created)); setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT); setConnectionMode(false); setConnectionSourceId(null); setConnectionSourceAnchor('auto'); setDashboardOpen(false);
  }, []);

  const refreshNotifications = useCallback(async () => {
    if (!serverSession) return;
    try { const result = await new ServerApi(serverBaseUrl).listNotifications(serverSession.token); setNotifications(result.notifications); setUnreadNotifications(result.unread); } catch { /* não bloqueia */ }
  }, [serverBaseUrl, serverSession]);

  const readNotification = useCallback(async (notification: UserNotification) => {
    if (!serverSession || notification.readAt) return;
    const readAt = new Date().toISOString();
    setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, readAt } : item));
    setUnreadNotifications((current) => Math.max(0, current - 1));
    try { await new ServerApi(serverBaseUrl).markNotificationRead(serverSession.token, notification.id); } catch { void refreshNotifications(); }
  }, [refreshNotifications, serverBaseUrl, serverSession]);

  const readAllNotifications = useCallback(async () => {
    if (!serverSession) return; const readAt = new Date().toISOString();
    setNotifications((current) => current.map((item) => item.readAt ? item : { ...item, readAt })); setUnreadNotifications(0);
    try { await new ServerApi(serverBaseUrl).markAllNotificationsRead(serverSession.token); } catch { void refreshNotifications(); }
  }, [refreshNotifications, serverBaseUrl, serverSession]);

  const openNotificationProject = useCallback(async (projectId: string) => {
    if (!serverSession) return;
    let project = remoteProjects.find((item) => item.id === projectId);
    if (!project) { try { const projects = await new ServerApi(serverBaseUrl).listProjects(serverSession.token); setRemoteProjects(projects); project = projects.find((item) => item.id === projectId); } catch { project = undefined; } }
    if (project) { setNotificationOpen(false); setDashboardOpen(false); await openRemoteProject(project); }
  }, [openRemoteProject, remoteProjects, serverBaseUrl, serverSession]);

  const handleProfileUpdated = useCallback((displayName: string) => {
    setServerSession((current) => {
      if (!current) return current; const next = { ...current, user: { ...current.user, displayName } };
      try { sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    setAccountInfo((current) => current ? { ...current, user: { ...current.user, displayName } } : current);
  }, []);

  const enterPresentation = useCallback(() => {
    setPresentationMode(true);
    try { const root = globalThis.document?.documentElement; if (root?.requestFullscreen) void root.requestFullscreen().catch(() => undefined); } catch {}
  }, []);
  const exitPresentation = useCallback(() => {
    setPresentationMode(false);
    try { if (globalThis.document?.fullscreenElement) void globalThis.document.exitFullscreen().catch(() => undefined); } catch {}
  }, []);

  const restoreServerRevision = useCallback(async (projectId: string, revision: ServerRevisionSummary) => {
    if (!serverSession || syncBusy) return;
    if (!window.confirm(`Restaurar a versão de ${new Date(revision.createdAt).toLocaleString('pt-BR')}? A versão atual continuará no histórico.`)) return;
    setSyncBusy(true); setSyncError(null);
    try {
      const result = await new ServerApi(serverBaseUrl).restoreRevision(serverSession.token, projectId, revision.revision);
      await adoptRemoteDocument(result.document, result.revision, serverSession?.user.id);
      setHistory(createHistory(result.document)); setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT);
      setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token));
      setServerPanelOpen(false); setDashboardOpen(false);
    } catch (error) { setSyncError(error instanceof Error ? error.message : 'Não foi possível restaurar a versão.'); }
    finally { setSyncBusy(false); }
  }, [serverBaseUrl, serverSession, syncBusy]);

  const continueEditorSafely = useCallback(async () => {
    if (!serverSession || !loaded || syncBusy) return;
    // O editor só pode continuar com algo pertencente ao escopo da identidade atual.
    if (remoteProjects.some((project) => project.id === document.id)) { setDashboardOpen(false); return; }
    const outbox = await listSyncOutbox(serverSession.user.id);
    if (outbox.some((item) => item.documentId === document.id)) { setDashboardOpen(false); return; }
    if (remoteProjects.length > 0) { await openRemoteProject(remoteProjects[0]); return; }
    setHistory(createHistory(createEmptyDocument()));
    setSelection(EMPTY_SELECTION);
    setViewport(INITIAL_VIEWPORT);
    setConnectionMode(false);
    setConnectionSourceId(null);
    setConnectionSourceAnchor('auto');
    setDashboardOpen(false);
  }, [document.id, loaded, openRemoteProject, remoteProjects, serverSession, syncBusy]);

  const openShareSafely = useCallback(async () => {
    if (!serverSession) { setEditorShareProject(null); openEditorOverlay('share'); return; }
    const saved = await saveCurrentNow();
    if (!saved.ok) {
      window.alert(`Este projeto continua salvo neste dispositivo e ainda não pode ser compartilhado.\n\n${saved.error || 'Não foi possível concluir a sincronização com a OWDEX.'}`);
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      window.alert('O compartilhamento exige conexão com a OWDEX. A cópia local continua segura neste dispositivo.');
      return;
    }
    try {
      const projects = await new ServerApi(serverBaseUrl).listProjects(serverSession.token);
      setRemoteProjects(projects);
      const target = projects.find((project) => project.id === saved.documentId);
      if (!target) {
        window.alert('A OWDEX confirmou o salvamento, mas o projeto ainda não apareceu na listagem da conta. Atualize a página e tente compartilhar novamente.');
        return;
      }
      setEditorShareProject(target);
      openEditorOverlay('share');
    } catch (error) {
      window.alert(error instanceof Error ? `Não foi possível confirmar o projeto na OWDEX antes de compartilhar.\n\n${error.message}` : 'Não foi possível confirmar o projeto na OWDEX antes de compartilhar.');
    }
  }, [openEditorOverlay, saveCurrentNow, serverBaseUrl, serverSession]);

  const openDashboardSafely = useCallback(async () => {
    closeEditorOverlays();
    await saveCurrentNow();
    await refreshLocalProjects(serverSession?.user.id);
    if (serverSession && typeof navigator !== 'undefined' && navigator.onLine !== false) {
      try { setRemoteProjects(await new ServerApi(serverBaseUrl).listProjects(serverSession.token)); }
      catch { /* a Home ainda mostra os projetos locais/recentes mesmo sem rede */ }
    }
    setDashboardOpen(true);
  }, [closeEditorOverlays, refreshLocalProjects, saveCurrentNow, serverBaseUrl, serverSession]);

  const activeOrganization = serverOrganizations.find((org) => org.id === syncOrganizationId) ?? serverOrganizations[0] ?? null;
  const activeOrganizationRole = activeOrganization?.role ?? null;
  const canOpenAdministration = serverSession?.user.role === 'master' || ['owner','admin'].includes(activeOrganizationRole ?? '');



  if (publicShareLoading) return <div className="app-loading-screen"><img className="brand-mark-image brand-mark-loading" src="/brand/owdex-draw-mark.png" alt="" /><strong>Abrindo projeto compartilhado…</strong></div>;
  if (publicShareError) return <div className="public-share-error"><img className="brand-mark-image brand-mark-loading" src="/brand/owdex-draw-mark.png" alt="" /><h1>Este link não está disponível</h1><p>{publicShareError}</p><a href="/">Ir para o OWDEX Draw</a></div>;
  if (publicShare) return <PublicShareViewer document={publicShare.document} allowCopy={publicShare.allowCopy} allowPdf={publicShare.allowPdf} ownerLabel={publicShare.ownerLabel} expiresAt={publicShare.expiresAt} />;
  if (productionLoginRequired() && authChecking) return <div className="app-loading-screen"><img className="brand-mark-image brand-mark-loading" src="/brand/owdex-draw-mark.png" alt="" /><strong>Validando sua sessão…</strong></div>;
  if (productionLoginRequired() && !serverSession) return <LoginScreen busy={syncBusy} error={syncError} baseUrl={serverBaseUrl} onLogin={loginServer} />;

  if (dashboardOpen && serverSession) return <Suspense fallback={<div className="app-loading-screen"><img className="brand-mark-image brand-mark-loading" src="/brand/owdex-draw-mark.png" alt="" /><strong>Carregando sua área de trabalho…</strong></div>}>
    <>
    <HomeDashboard
      session={serverSession}
      organizations={serverOrganizations}
      account={accountInfo}
      projects={dashboardProjects}
      busy={syncBusy || !loaded}
      unreadNotifications={unreadNotifications}
      organizationRole={activeOrganizationRole}
      onAdmin={() => setMasterOpen(true)}
      onOpenProject={(project) => void openDashboardProject(project)}
      onNew={() => { const next=createEmptyDocument(); setHistory(createHistory(next)); setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT); setDashboardOpen(false); }}
      onUseTemplate={startTemplateFromDashboard}
      onManageProjects={() => { setDashboardOpen(false); openEditorOverlay('projects'); }}
      onAccount={() => openEditorOverlay('account')}
      onNotifications={() => openEditorOverlay('notifications')}
      onUpdatePreference={(project,input) => void updateProjectPreference(project,input)}
      onSyncProject={(project) => void syncDashboardProject(project)}
      onContinueEditor={() => void continueEditorSafely()}
      onSaveProject={(project) => void downloadDashboardProject(project)}
      onSaveAsProject={(project,title) => void duplicateDashboardProject(project,title)}
      onRenameProject={(project,title) => void renameDashboardProject(project,title)}
      onShareProject={(project) => {
        setAccountOpen(false);
        setNotificationOpen(false);
        void (async () => {
          if (!serverSession) {
            setDashboardOpen(false);
            openEditorOverlay('server');
            return;
          }
          let target = project;
          if (project.localOnly) {
            await syncDashboardProject(project);
            const projects = await new ServerApi(serverBaseUrl).listProjects(serverSession.token);
            setRemoteProjects(projects);
            target = projects.find((item) => item.id === project.id)
              ?? projects.filter((item) => item.title === project.title).sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0]
              ?? project;
          }
          if (target.localOnly) {
            window.alert('O projeto ainda está apenas neste dispositivo. Salve na OWDEX e tente compartilhar novamente.');
            return;
          }
          setDashboardShareProject(target);
        })().catch((error) => window.alert(error instanceof Error ? error.message : 'Não foi possível preparar o compartilhamento deste projeto.'));
      }}
      onDeleteProject={(project) => void trashDashboardProject(project)}
    />
    {dashboardShareProject && <SharePanel open={Boolean(dashboardShareProject)} baseUrl={serverBaseUrl} session={serverSession} projectId={dashboardShareProject.id} projectTitle={dashboardShareProject.title} permission={dashboardShareProject.permission} organizationRole={dashboardShareProject.organization_role ?? activeOrganizationRole} onClose={() => setDashboardShareProject(null)} onOpenServer={() => { setDashboardShareProject(null); setDashboardOpen(false); openEditorOverlay('server'); }} />}
    {accountOpen && <AccountPanel open={accountOpen} baseUrl={serverBaseUrl} session={serverSession} onClose={() => setAccountOpen(false)} onProfileUpdated={handleProfileUpdated} onLogout={() => void logoutServer()} />}
    {notificationOpen && <NotificationCenter open={notificationOpen} session={serverSession} notifications={notifications} announcements={announcements} unread={unreadNotifications} busy={announcementBusy} onClose={() => setNotificationOpen(false)} onRead={(item) => void readNotification(item)} onReadAll={() => void readAllNotifications()} onOpenProject={(id) => void openNotificationProject(id)} onDismissAnnouncement={(item) => void dismissAnnouncement(item)} />}
    {masterOpen && canOpenAdministration && <MasterPanel open={masterOpen} baseUrl={serverBaseUrl} session={serverSession} organizationRole={activeOrganizationRole} onClose={() => setMasterOpen(false)} />}
    </>
  </Suspense>;

  const selectedCount = selection.nodeIds.length;
  const selectedLocked = selectedCount > 0 && page.nodes.filter((node) => selection.nodeIds.includes(node.id)).every((node) => node.locked);
  const canUngroup = page.nodes.some((node) => selection.nodeIds.includes(node.id) && Boolean(node.groupId));

  return (
    <div className={`app-shell ${announcements.length ? 'has-announcement' : ''} ${presentationMode ? 'presentation-mode' : ''}`}>
      <a className="skip-link" href="#diagram-editor">Ir para o canvas</a>
      <div className="sr-only" aria-live="polite">{saveStatus === 'saved' ? 'Projeto salvo localmente.' : saveStatus === 'saving' ? 'Salvando projeto.' : 'Falha no salvamento local.'} {selectedCount > 0 ? `${selectedCount} objeto(s) selecionado(s).` : ''}</div>
      <TopBar
        document={document}
        readOnly={!canEditCurrent}
        canUndo={history.past.length > 0}
        canRedo={history.future.length > 0}
        saveStatus={saveStatus}
        onSave={() => void saveCurrentNow()}
        connectionMode={canEditCurrent && connectionMode}
        selectedCount={selectedCount}
        selectedEdgeCount={selection.edgeIds.length}
        selectedLocked={selectedLocked}
        canUngroup={canUngroup}
        canBundleEdges={canBundleEdges}
        canUnbundleEdges={canUnbundleEdges}
        onUndo={() => setHistory((current) => undo(current))}
        onRedo={() => setHistory((current) => redo(current))}
        onNew={newDocument}
        onProjects={() => { void openDashboardSafely(); }}
        onLayers={() => openEditorOverlay('layers')}
        onValidate={() => openEditorOverlay('validation')}
        onCommands={() => openEditorOverlay('commands')}
        onSearch={() => openEditorOverlay('search')}
        onHistory={() => openEditorOverlay('history')}
        onHelp={() => openEditorOverlay('help')}
        onServer={() => openEditorOverlay('server')}
        onComments={() => openEditorOverlay('comments')}
        onImport={(next) => { setHistory(createHistory(next)); setSelection(EMPTY_SELECTION); setViewport(INITIAL_VIEWPORT); setConnectionMode(false); setConnectionSourceId(null); setConnectionSourceAnchor('auto'); }}
        onImportOrgCsv={(text) => void importOrgChartCsv(text)}
        onImportSql={(text) => void importSqlErd(text)}
        onImportNetworkCsv={(text) => void importNetworkInventoryCsv(text)}
        onImportMermaid={(text) => void importMermaidFlowchart(text)}
        onImportDataCsv={importDataCsvToCurrentPage}
        onImportPlantUml={(text) => void importPlantUmlDiagram(text)}
        onImportDot={(text) => void importDotDiagram(text)}
        onImportBpmnXml={(text) => void importBpmnXmlDiagram(text)}
        onImportCloudCsv={(text) => void importCloudInventoryCsv(text)}
        onExportWorkspace={() => void exportLocalWorkspace()}
        onImportWorkspace={(text) => void restoreLocalWorkspace(text)}
        onToggleConnection={() => { setConnectionMode((value) => !value); setConnectionSourceId(null); setConnectionSourceAnchor('auto'); }}
        onArrange={(direction) => void arrange(direction)}
        onAlign={alignSelected}
        onDistribute={distributeSelected}
        onMatchSize={matchSizeSelected}
        onGroup={groupSelected}
        onUngroup={ungroupSelected}
        onConnectSelection={connectSelectedNodes}
        onSelectConnectedEdges={selectConnectedEdges}
        onBundleEdges={bundleSelectedEdges}
        onUnbundleEdges={unbundleSelectedEdges}
        onToggleLock={toggleLockSelected}
        onLayer={moveLayer}
        onDuplicate={duplicateSelected}
        onDelete={deleteSelected}
        onFit={fitContent}
        onToggleLibrary={(requestedTab) => { const nextTab = requestedTab ?? libraryTab; setLibraryTab(nextTab); if (window.innerWidth <= 760) { setMobileLibraryOpen(true); setMobilePropertiesOpen(false); } else { window.document.getElementById('library-panel')?.focus(); window.setTimeout(() => window.document.querySelector<HTMLElement>('[data-library-active="true"]')?.focus(), 0); } }}
        onToggleProperties={() => { if (window.innerWidth <= 760) { setMobilePropertiesOpen((value) => !value); setMobileLibraryOpen(false); } else window.document.getElementById('properties-panel')?.focus(); }}
        onShare={() => void openShareSafely()}
        onHome={() => { void openDashboardSafely(); }}
        onNotifications={() => openEditorOverlay('notifications')}
        unreadNotifications={unreadNotifications}
        onPresentation={enterPresentation}
        userDisplayName={serverSession?.user.displayName ?? null}
        userEmail={serverSession?.user.email ?? null}
        userRole={serverSession?.user.role ?? null}
        organizationRole={activeOrganizationRole}
        organizationName={serverOrganizations.find((org) => org.id === syncOrganizationId)?.name ?? serverOrganizations[0]?.name ?? null}
        planCode={serverOrganizations.find((org) => org.id === syncOrganizationId)?.planCode ?? serverOrganizations[0]?.planCode ?? null}
        planStatus={serverOrganizations.find((org) => org.id === syncOrganizationId)?.planStatus ?? serverOrganizations[0]?.planStatus ?? null}
        onAccount={() => openEditorOverlay('account')}
        onMaster={() => openEditorOverlay('master')}
        onLogout={() => void logoutServer()}
      />
      <AnnouncementCenter announcements={announcements} busy={announcementBusy} onDismiss={(announcement) => void dismissAnnouncement(announcement)} />
      {presentationMode && <button type="button" className="presentation-exit" onClick={exitPresentation}>Sair da apresentação <kbd>Esc</kbd></button>}
      <div className="workspace">
        {(mobileLibraryOpen || mobilePropertiesOpen) && <button type="button" className="mobile-panel-backdrop" aria-label="Fechar painel lateral" onClick={() => { setMobileLibraryOpen(false); setMobilePropertiesOpen(false); }} />}
        <div className={`panel-slot panel-slot-left ${mobileLibraryOpen ? 'is-open' : ''}`}>
          <button type="button" className="mobile-panel-close" aria-label="Fechar biblioteca" onClick={() => setMobileLibraryOpen(false)}>×</button>
        <ShapePalette
          readOnly={!canEditCurrent} activeTab={libraryTab} onTabChange={setLibraryTab} onAdd={addNode} onApplyTemplate={applyTemplate}
          customSymbols={customSymbols} onAddCustom={addCustomSymbol} onDeleteCustom={(id) => setCustomSymbols(deleteCustomSymbol(id))}
          onExportCustom={() => downloadBlob('OWDEX_DRAW_MINHA_BIBLIOTECA.json', new Blob([exportCustomSymbolsJson()], { type: 'application/json' }))}
          onImportCustom={(text) => { try { setCustomSymbols(importCustomSymbolsJson(text)); } catch (error) { window.alert(error instanceof Error ? error.message : 'Biblioteca inválida.'); } }}
          customTemplates={customTemplates} onApplyCustomTemplate={applyCustomTemplate} onDeleteCustomTemplate={(id) => setCustomTemplates(deleteCustomTemplate(id))}
          onExportCustomTemplates={() => downloadBlob('OWDEX_DRAW_MEUS_MODELOS.json', new Blob([exportCustomTemplatesJson()], { type: 'application/json' }))}
          onImportCustomTemplates={(text) => { try { setCustomTemplates(importCustomTemplatesJson(text)); } catch (error) { window.alert(error instanceof Error ? error.message : 'Arquivo de modelos inválido.'); } }}
          onSaveCurrentPageTemplate={saveCurrentPageAsTemplate}
          organizationTemplates={organizationTemplates}
          organizationName={serverOrganizations.find((org) => org.id === syncOrganizationId)?.name}
          onApplyOrganizationTemplate={applyOrganizationTemplate}
          onApplyOrganizationTemplateAsPage={applyOrganizationTemplateAsPage}
          onUpdateOrganizationTemplate={(preset) => void updateOrganizationTemplate(preset)}
          onDeleteOrganizationTemplate={(preset) => void deleteOrganizationTemplate(preset)}
          onSaveOrganizationTemplate={serverSession && syncOrganizationId ? () => void saveOrganizationTemplate() : undefined}
          onRefreshOrganizationTemplates={serverSession && syncOrganizationId ? () => void refreshOrganizationTemplates() : undefined}
        />
        </div>
        <DiagramCanvas
          page={page}
          selectedNodeIds={selection.nodeIds}
          selectedEdgeIds={selection.edgeIds}
          viewport={viewport}
          connectionMode={canEditCurrent && connectionMode}
          connectionSourceId={connectionSourceId}
          connectionSourceAnchor={connectionSourceAnchor}
          preferences={preferences}
          readOnly={!canEditCurrent}
          onSelectNode={selectNode}
          onSelectEdge={selectEdge}
          onSelectArea={selectArea}
          onMoveNodes={moveNodes}
          onResizeNode={resizeNode}
          onTransformNodes={transformNodes}
          onRotateNode={rotateNode}
          onViewport={setViewport}
          onNodeForConnection={handleConnectionNode}
          onEditNodeText={editNodeText}
          onEditEdgeLabel={editEdgeLabel}
          onUpdateEdgeWaypoints={updateEdgeWaypoints}
          onDropShape={dropShape}
          onNavigateNodeLink={activatePage}
          onContextMenuRequest={setContextMenu}
          remoteUsers={realtimeUsers}
          onCursor={(point) => realtimeClient.current?.setCursor(point)}
          commentCounts={commentCounts}
          renderThreshold={capabilities.renderThreshold}
          cullingMarginPx={capabilities.cullingMarginPx}
          adaptiveQuality={capabilities.tier !== 'alto'}
          onOpenNodeComments={(id) => { setSelection({ nodeIds: [id], edgeIds: [] }); openEditorOverlay('comments'); }}
        />
        <div className={`panel-slot panel-slot-right ${mobilePropertiesOpen ? 'is-open' : ''}`}>
          <button type="button" className="mobile-panel-close" aria-label="Fechar propriedades" onClick={() => setMobilePropertiesOpen(false)}>×</button>
        <PropertiesPanel
          readOnly={!canEditCurrent}
          node={selectedNode}
          edge={selectedEdge}
          selectedEdges={selectedEdges}
          selectedEdgeCount={selection.edgeIds.length}
          isBpmnEdge={selectedEdgeIsBpmn}
          documentTitle={document.title}
          pageName={page.name}
          currentPageId={page.id}
          pages={document.pages.map((item) => ({ id: item.id, name: item.name }))}
          selectionCount={selection.nodeIds.length}
          selectedNodes={selectedNodeList}
          pageNodes={page.nodes}
          onDocumentTitle={(title) => commitDocument({ ...document, title, updatedAt: new Date().toISOString() })}
          onPageName={(name) => renamePage(page.id, name)}
          onNavigatePage={activatePage}
          onPatchNode={patchSelectedNode}
          onPatchEdge={patchSelectedEdge}
          onPatchSelectedEdges={patchSelectedEdges}
          onReverseEdge={reverseSelectedEdge}
          onPatchSelectionStyle={patchSelectionStyle}
          onPatchSelectedNodes={patchSelectedNodes}
          onAddOrgChild={addOrgChild}
          onArrangeOrg={() => void arrangeOrg()}
          onAddMindChild={addMindChild}
          onArrangeMind={() => void arrangeMind()}
          onNormalizeBpmn={normalizeBpmnSwimlanes}
          onSaveToLibrary={saveNodeToLibrary}
        />
        </div>
      </div>
      <PageTabs
        pages={document.pages}
        activePageId={document.activePageId}
        onActivate={activatePage}
        onAdd={addPage}
        onRename={renamePage}
        onDuplicate={duplicatePage}
        onDelete={deletePage}
        onMove={movePage}
      />
      <StatusBar
        zoom={viewport.zoom}
        saveStatus={saveStatus}
        capabilities={capabilities}
        nodeCount={page.nodes.length}
        edgeCount={page.edges.length}
        pendingSyncCount={pendingSyncCount}
        serverConnected={Boolean(serverSession)}
        cloudSaved={remoteProjects.some((project) => project.id === document.id)}
        cloudUpdatedAt={remoteProjects.find((project) => project.id === document.id)?.updated_at ?? null}
        offline={typeof navigator !== 'undefined' && navigator.onLine === false}
        realtimeConnected={realtimeConnected}
        collaboratorCount={realtimeUsers.length}
        remoteUpdateAvailable={remoteUpdateAvailable}
        readOnly={!canEditCurrent}
        preferences={preferences}
        onPreferences={setPreferences}
        onZoom={(zoom) => setViewport((current) => ({ ...current, zoom }))}
        onReviewRemoteUpdate={() => void reviewRemoteUpdate()}
        onCollaborators={() => openEditorOverlay('collaborators')}
        onSyncNow={() => void (async () => { await saveCurrentNow(); await synchronizeNow(); })()}
        syncBusy={syncBusy}
      />
      <Suspense fallback={null}>{projectsOpen && <ProjectManager currentId={document.id} baseUrl={serverBaseUrl} session={serverSession} remoteProjects={remoteProjects} onClose={() => setProjectsOpen(false)} onOpenLocal={(next) => void openProject(next)} onOpenRemote={(project) => void openRemoteProject(project)} onRefreshRemote={() => refreshRemoteProjects()} />}</Suspense>
      <Suspense fallback={null}>{commandsOpen && <CommandPalette onClose={() => setCommandsOpen(false)} onAddShape={addNode} onApplyTemplate={applyTemplate} onAction={runCommand} />}</Suspense>
      <Suspense fallback={null}>{layersOpen && <LayersManager page={page} selectedNodeIds={selection.nodeIds} onClose={() => setLayersOpen(false)} onSelect={(id) => selectNode(id)} onToggleHidden={toggleNodeHidden} onToggleLocked={toggleNodeLockedById} onMoveLayer={moveLayerById} />}</Suspense>
      <Suspense fallback={null}>{validationOpen && <ValidationPanel document={document} issues={validationIssues} onClose={() => setValidationOpen(false)} onSelect={selectValidationIssue} />}</Suspense>
      <Suspense fallback={null}>{searchOpen && <ProjectSearch document={document} onClose={() => setSearchOpen(false)} onOpen={openSearchResult} />}</Suspense>
      <Suspense fallback={null}>{historyOpen && <HistoryPanel current={document} snapshots={history.past} onClose={() => setHistoryOpen(false)} onRestore={restoreHistorySnapshot} />}</Suspense>
      <Suspense fallback={null}>{helpOpen && <HelpPanel onClose={() => setHelpOpen(false)} />}</Suspense>
      <Suspense fallback={null}>{serverPanelOpen && <ServerPanel
        open={serverPanelOpen}
        baseUrl={serverBaseUrl}
        session={serverSession}
        organizations={serverOrganizations}
        syncOrganizationId={syncOrganizationId}
        organizationRole={activeOrganizationRole}
        onSyncOrganization={setSyncOrganizationId}
        pendingCount={pendingSyncCount}
        busy={syncBusy}
        lastResult={syncLastResult}
        error={syncError}
        remoteProjects={remoteProjects}
        conflicts={syncConflicts}
        onClose={() => setServerPanelOpen(false)}
        onBaseUrl={setServerBaseUrl}
        onLogin={loginServer}
        onLogout={logoutServer}
        onSync={synchronizeNow}
        onRefreshProjects={refreshRemoteProjects}
        onOpenRemote={openRemoteProject}
        onOpenRevision={openServerRevision}
        onCompareRevision={compareServerRevision}
        onRestoreRevision={restoreServerRevision}
        onMaster={() => openEditorOverlay('master')}
      />}</Suspense>
      <Suspense fallback={null}>{shareOpen && <SharePanel open={shareOpen} baseUrl={serverBaseUrl} session={serverSession} projectId={editorShareProject?.id ?? document.id} projectTitle={editorShareProject?.title ?? document.title} permission={editorShareProject?.permission ?? currentRemotePermission} organizationRole={editorShareProject?.organization_role ?? currentProjectOrganizationRole ?? activeOrganizationRole} onClose={() => { setShareOpen(false); setEditorShareProject(null); }} onOpenServer={() => openEditorOverlay('server')} />}</Suspense>
      <Suspense fallback={null}>{masterOpen && canOpenAdministration && <MasterPanel open={masterOpen} baseUrl={serverBaseUrl} session={serverSession} organizationRole={activeOrganizationRole} onClose={() => setMasterOpen(false)} />}</Suspense>
      <Suspense fallback={null}>{accountOpen && <AccountPanel open={accountOpen} baseUrl={serverBaseUrl} session={serverSession} onClose={() => setAccountOpen(false)} onProfileUpdated={handleProfileUpdated} onLogout={() => void logoutServer()} />}</Suspense>
      <Suspense fallback={null}>{notificationOpen && <NotificationCenter open={notificationOpen} session={serverSession} notifications={notifications} announcements={announcements} unread={unreadNotifications} busy={announcementBusy} onClose={() => setNotificationOpen(false)} onRead={(item) => void readNotification(item)} onReadAll={() => void readAllNotifications()} onOpenProject={(id) => void openNotificationProject(id)} onDismissAnnouncement={(item) => void dismissAnnouncement(item)} />}</Suspense>
      <Suspense fallback={null}>{collaboratorsOpen && <CollaboratorsPanel open={collaboratorsOpen} users={realtimeUsers} activePageId={document.activePageId} onClose={() => setCollaboratorsOpen(false)} onFocus={focusCollaborator} />}</Suspense>
      <Suspense fallback={null}>{revisionCompare && <RevisionComparePanel
        local={revisionCompare.local}
        remote={revisionCompare.remote}
        remoteRevision={revisionCompare.revision}
        onClose={() => setRevisionCompare(null)}
        onKeepLocal={() => void keepComparedLocal()}
        onOpenRemoteCopy={() => void openRemoteCompareCopy()}
        onAdoptRemoteSafely={() => void adoptComparedRemoteSafely()}
      />}</Suspense>
      <Suspense fallback={null}>{commentsOpen && <CommentsPanel
        open={commentsOpen}
        baseUrl={serverBaseUrl}
        session={serverSession}
        document={document}
        selectedNodeId={selection.nodeIds.length === 1 ? selection.nodeIds[0] : null}
        refreshKey={commentEventVersion}
        onClose={() => setCommentsOpen(false)}
        onOpenServer={() => openEditorOverlay('server')}
        onSelectNode={(pageId, nodeId) => { if (pageId !== document.activePageId) activatePage(pageId); setSelection({ nodeIds: [nodeId], edgeIds: [] }); setCommentsOpen(false); }}
      />}</Suspense>
      {contextMenu && <CanvasContextMenu
        request={contextMenu}
        selectedCount={selection.nodeIds.length}
        selectedEdgeCount={selection.edgeIds.length}
        selectedLocked={selectedLocked}
        canUngroup={canUngroup}
        canBundleEdges={canBundleEdges}
        canUnbundleEdges={canUnbundleEdges}
        canPaste={clipboard.current.nodes.length > 0}
        onClose={() => setContextMenu(null)}
        onDuplicate={duplicateSelected}
        onInsertAfter={insertNextStep}
        canSmartDelete={Boolean(smartDeleteInfo)}
        onSmartDelete={deleteAndReconnect}
        onDelete={deleteSelected}
        onToggleLock={toggleLockSelected}
        onFront={() => moveLayer('front')}
        onBack={() => moveLayer('back')}
        onGroup={groupSelected}
        onUngroup={ungroupSelected}
        onCopyStyle={copySelectedStyle}
        onApplyStyle={applyCopiedStyle}
        canApplyStyle={styleClipboardReady && selection.nodeIds.length > 0}
        onBundleEdges={bundleSelectedEdges}
        onUnbundleEdges={unbundleSelectedEdges}
        onConnect={() => { if(selection.nodeIds.length>1){connectSelectedNodes();return;} setConnectionMode(true); setConnectionSourceId(selection.nodeIds[0] ?? null); setConnectionSourceAnchor('auto'); }}
        onPaste={pasteClipboard}
        onFit={fitContent}
        onNewPage={addPage}
      />}
    </div>
  );
}
